import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useNavbarSubtitulo } from '../../context/NavbarSubtituloContext';
import { buildApiUrl } from '../../utils/apiUrl';
import { ConexionBanner } from '../../components/ConexionBanner';
import { VistaVoBo } from './VistaVoBo';
import type { AppBoleta, AppBoletaProducto, AppBoletaAjuste, AppBoletaAjusteSolicitudEstado } from '../../types/empacadora';
import { calcularBoleta } from '../../types/planManejo';
import { selectCls } from '../../components/ui';
import { RAZONES_AJUSTE } from '../../types/empacadora';

// ── CSS animaciones ───────────────────────────────────────────────────────────

const BODEGA_CSS_ID = 'bodega-glow-styles';

function injectBodegaCSS() {
  const css = `
    @keyframes bod-check-glow {
      0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
      50%  { box-shadow: 0 0 0 8px rgba(34,197,94,0.35), 0 0 20px 6px rgba(34,197,94,0.18); }
      100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
    }
    @keyframes bod-pending-pulse {
      0%   { box-shadow: 0 0 0 0 rgba(148,163,184,0); }
      50%  { box-shadow: 0 0 0 5px rgba(148,163,184,0.2); }
      100% { box-shadow: 0 0 0 0 rgba(148,163,184,0); }
    }
    .bod-check-done { animation: bod-check-glow   1.8s ease-in-out infinite; }
    .bod-check-pend { animation: bod-pending-pulse 2.4s ease-in-out infinite; }
    @keyframes bod-row-approved-glow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); outline: 0 solid rgba(34,197,94,0); }
      45%      { box-shadow: 0 0 0 5px rgba(34,197,94,0.32), 0 0 24px rgba(34,197,94,0.45); outline: 2px solid rgba(134,239,172,0.9); }
    }
    .bod-row-approved-flash { animation: bod-row-approved-glow 1.15s ease-in-out 5; }
    @keyframes bod-row-rejected-glow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); outline: 0 solid rgba(239,68,68,0); }
      45%      { box-shadow: 0 0 0 5px rgba(239,68,68,0.28), 0 0 24px rgba(239,68,68,0.42); outline: 2px solid rgba(252,165,165,0.9); }
    }
    .bod-row-rejected-flash { animation: bod-row-rejected-glow 1.15s ease-in-out 5; }
    @keyframes bod-toast-in {
      0%   { transform: translateX(100%); opacity: 0; }
      100% { transform: translateX(0);    opacity: 1; }
    }
    /* ── Responsive: Preparar Despacho card en móvil ─────────────────────── */
    @media (max-width: 720px) {
      /* Wrapper de Despacho: reducir padding y prevenir overflow horizontal */
      .bod-despacho-wrap {
        padding: 10px !important;
        box-sizing: border-box;
        max-width: 100%;
        overflow-x: hidden;
      }
      .bod-prod-row {
        grid-template-columns: 1fr !important;
        row-gap: 10px !important;
        box-sizing: border-box;
        max-width: 100%;
        overflow: hidden;
        padding: 10px !important;
      }
      .bod-prod-row > * { min-width: 0; }
      .bod-prod-row, .bod-prod-row * {
        word-break: break-word;
        overflow-wrap: anywhere;
      }
      /* Bloque derecho: 2 filas (Planificado / Cambio) × 2 columnas (etiqueta-izq | valor-der). */
      .bod-prod-right {
        grid-template-columns: 1fr auto !important;
        grid-template-rows: auto auto auto;
        row-gap: 8px !important;
        column-gap: 12px !important;
        align-items: center !important;
        width: 100%;
      }
      /* Fila 1 — PLANIFICADO: etiqueta (caja) izq, número der */
      .bod-prod-plan {
        display: contents !important;
      }
      .bod-prod-plan-label {
        grid-column: 1; grid-row: 1;
        font-size: 12px !important;
        font-weight: 700 !important;
        color: var(--ink-muted) !important;
        text-align: left !important;
        padding: 8px 12px !important;
        border: 1px solid var(--line);
        border-radius: 8px;
        background: rgba(147,197,253,0.06);
        margin: 0 !important;
        text-transform: none !important;
      }
      .bod-prod-plan-num {
        grid-column: 2; grid-row: 1;
        font-size: 18px !important;
        text-align: right;
      }
      /* Fila 2 — CAMBIO: etiqueta (caja) izq, input der */
      .bod-prod-input-label {
        display: flex !important;
        align-items: center;
        grid-column: 1; grid-row: 2;
        font-size: 12px;
        font-weight: 700;
        color: var(--ink-muted);
        padding: 8px 12px;
        border: 1px solid var(--line);
        border-radius: 8px;
        background: rgba(147,197,253,0.06);
      }
      .bod-prod-input-wrap {
        grid-column: 2; grid-row: 2;
        justify-self: end;
      }
      .bod-prod-input {
        width: 120px !important;
        font-size: 18px !important;
      }
      /* Fila 3 — Motivo full-width */
      .bod-prod-motivo {
        grid-column: 1 / -1 !important;
        grid-row: 3;
        width: 100%;
      }
    }
  `;
  const existing = document.getElementById(BODEGA_CSS_ID);
  if (existing) {
    if (existing.textContent !== css) existing.textContent = css;
    return;
  }
  const s = document.createElement('style');
  s.id = BODEGA_CSS_ID;
  s.textContent = css;
  document.head.appendChild(s);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtN(n?: number | null, dec = 2) {
  if (n == null) return '—';
  return n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: 3 });
}

function fmtFecha(s?: string | null) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Quita los ceros a la izquierda del número de boleta para mostrar (solo display).
// "0000004734" -> "4734". No cambia el valor real almacenado.
function numBoletaDisplay(n?: string | null) {
  const s = String(n ?? '').replace(/^0+/, '');
  return s || String(n ?? '');
}

// Color por estado usando las variables --tone-* (semánticas pero adaptadas al
// tema claro/oscuro por applyPalette). El estado mantiene su color con significado;
// el fondo/contraste se adapta al tema elegido.
function estadoColor(estado: string): { bg: string; text: string; border: string } {
  switch (estado) {
    case 'pendiente':  return { bg: 'var(--tone-info-bg)', text: 'var(--tone-info-text)', border: 'var(--tone-info-border)' };
    case 'en_proceso': return { bg: 'var(--tone-warn-bg)', text: 'var(--tone-warn-text)', border: 'var(--tone-warn-border)' };
    case 'aplicada':   return { bg: 'var(--tone-success-bg)', text: 'var(--tone-success-text)', border: 'var(--tone-success-border)' };
    case 'archivada':  return { bg: 'var(--tone-success-bg)', text: 'var(--tone-success-text)', border: 'var(--tone-success-border)' };
    case 'cancelada':  return { bg: 'var(--tone-warn-bg)', text: '#ef4444', border: 'var(--tone-warn-border)' };
    default:           return { bg: 'var(--surface-overlay)', text: 'var(--ink-muted)', border: 'var(--line)' };
  }
}

function estadoLabel(estado: string) {
  const map: Record<string, string> = {
    borrador: 'Borrador', pendiente: 'Pendiente', en_proceso: 'En proceso',
    aplicada: 'Aplicada', archivada: 'Archivada', cancelada: 'Cancelada',
  };
  return map[estado] ?? estado;
}

// Clasifica una boleta en un tab por AVANCE de booms. Función pura (recibe el
// conteo de booms despachados) para poder usarse antes de declarar el state.
function perteneceATabPura(
  b: AppBoleta,
  tab: 'pendiente' | 'en_proceso' | 'aplicada',
  boomsDespCount: Map<number, number>,
): boolean {
  const estado = String(b.estado || '').toLowerCase();
  if (tab === 'aplicada') return estado === 'aplicada' || estado === 'archivada';
  if (estado === 'aplicada' || estado === 'archivada') return false;
  const totalBooms = Number(b.n_booms_completos || 0) + (Number(b.litros_fraccion || 0) > 0 ? 1 : 0);
  const cerrados = boomsDespCount.get(Number(b.id)) ?? 0;
  if (tab === 'pendiente') return totalBooms === 0 || cerrados < totalBooms;
  return cerrados > 0;
}

// ── Tipos locales ─────────────────────────────────────────────────────────────

interface ProdConAjuste extends AppBoletaProducto {
  cantidad_real: number;
  cantidad_raw: string;   // valor string mientras el usuario edita
  razon_codigo: string;
  razon_detalle: string;
  ajustado: boolean;
  solicitud_id?: number;
  solicitud_estado?: AppBoletaAjusteSolicitudEstado;
  solicitud_tipo?: string;
  cantidad_aprobada?: number | null;
  respuesta_comentario?: string | null;
  // Distribución (puede ser la planificada o la redistribuida si hay ajuste)
  cantidad_boom_efectiva?: number;
  cantidad_fraccion_efectiva?: number;
}

// Tanda de mezcla: boletas pendientes con fórmula idéntica (mismo plan/ciclo) el
// mismo día. El bodeguero prepara UNA mezcla con el total consolidado de cada
// producto, y ve cuánto va a cada lote (columna por lote en la tabla dinámica).
interface TandaProducto {
  articulo: string;
  nombre: string;
  dosis: number;
  medida: string;
  porBoleta: Record<number, number>;  // boleta_id -> cantidad para ese lote
  total: number;                      // suma de todas las boletas de la tanda
}
interface TandaGrupo {
  firma: string;
  cicloNombre: string;
  boletas: { id: number; numero: string; lote_num: string; etapa_nombre: string }[];
  productos: TandaProducto[];
  aguaPorBoleta: Record<number, number>;
  aguaTotal: number;
  aguaMedida: string;
  aguaFormula: number;   // volumen base de la fórmula (dosis del agua), p.ej. 1500
}

// Redistribuye una cantidad total entre boom completo y fracción manteniendo
// la DOSIS POR BOOM original. El resto va a la fracción.
// Ejemplo: si dosis = 12 L/boom y aprobado = 17 L → 1 boom × 12 L + fracción 5 L
//          si dosis = 12 L/boom y aprobado = 30 L → 2 booms × 12 L + fracción 6 L
//          si dosis = 12 L/boom y aprobado =  8 L → 0 booms + fracción 8 L
// Mantiene la concentración correcta en cada boom completo y absorbe el
// remanente (positivo o negativo) en la fracción.
function redistribuirBoomFraccion(
  cantidadTotal: number,
  cantidadBoomPlan: number,
  _cantidadFraccionPlan: number,
  nBoomsCompletos: number,
): { cantidad_boom: number; cantidad_fraccion: number } {
  if (cantidadBoomPlan <= 0 || nBoomsCompletos <= 0) {
    return { cantidad_boom: 0, cantidad_fraccion: cantidadTotal };
  }
  // Mantener la dosis estándar por boom y poner el resto en fracción
  const cantidadBoom = cantidadBoomPlan;
  const cantidadFraccion = cantidadTotal - (cantidadBoom * nBoomsCompletos);
  return { cantidad_boom: cantidadBoom, cantidad_fraccion: cantidadFraccion };
}

type Vista = 'lista' | 'despacho' | 'confirmacion' | 'vob' | 'tandas';

// ── Componente principal ──────────────────────────────────────────────────────

export function BodegaPage() {
  const empresaId = useEmpresaId();
  const [usuario, setUsuario]         = useState('');
  const [vista, setVista]             = useState<Vista>('lista');
  const [boletas, setBoletas]         = useState<AppBoleta[]>([]);
  const [boletaSel, setBoletaSel]     = useState<AppBoleta | null>(null);
  const [productos, setProductos]     = useState<ProdConAjuste[]>([]);
  // Reporte de campo del boomero (necesario para confirmar aplicacion)
  const [reporteCampo, setReporteCampo] = useState<{
    id: number;
    registrado_por?: string | null;
    updated_at?: string | null;
    fin_at?: string | null;
    inicio_at?: string | null;
    inicio_lat?: number | null;
    inicio_lng?: number | null;
    fin_lat?: number | null;
    fin_lng?: number | null;
  } | null>(null);
  // Geojson del lote para mapa en V°B°
  const [loteGeojson, setLoteGeojson] = useState<any | null>(null);
  // Tracking GPS del recorrido (V°B°)
  const [tracks, setTracks] = useState<import('../../types/empacadora').AppBoletaCampoTrack[]>([]);
  const [anchoBoom, setAnchoBoom] = useState<number | null>(null);
  const [loading, setLoading]         = useState(false);
  const [guardando, setGuardando]     = useState(false);
  const [msg, setMsg]                 = useState<{ tipo: 'ok' | 'err'; texto: string } | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<'pendiente' | 'en_proceso' | 'aplicada'>('pendiente');
  const [busqueda, setBusqueda]       = useState('');
  const [filtroDia, setFiltroDia]     = useState('');   // fecha_plan; '' = todos los días
  // Tandas de mezcla: boletas pendientes con fórmula idéntica el mismo día.
  const [tandasData, setTandasData]   = useState<TandaGrupo[] | null>(null);
  const [tandasLoading, setTandasLoading] = useState(false);
  // Boletas que pertenecen a alguna tanda (para marcarlas en la lista). Se llena
  // al cargar la lista (cálculo liviano sobre los productos ya disponibles).
  const [boletasEnTanda, setBoletasEnTanda] = useState<Map<number, { firma: string; n: number }>>(new Map());
  // Equipos (booms) disponibles y la capacidad elegida (global para todas las tandas).
  const [equipos, setEquipos] = useState<{ id: number; nombre: string; capacidad_l: number }[]>([]);
  const [capacidadEquipo, setCapacidadEquipo] = useState(6000);
  // Productos ya echados al tanque por el bodeguero (checklist por tanda).
  // Clave: `${firma}#${articulo}`.
  const [tandaCheck, setTandaCheck] = useState<Set<string>>(new Set());

  const [checkeados, setCheckeados]   = useState<Set<number>>(new Set());
  // Despacho por boom: boomActivo = número del boom que está preparando (1..N o 0=fracción)
  // boomsCerrados: lista de booms ya despachados/descartados
  // Cada item trae sub-estados: lado bodega (despachado/estado) y lado boomero (aplicado_inicio_at/aplicado_fin_at)
  interface BoomDesp {
    boom_num: number;
    tipo: 'completo' | 'fraccion';
    despachado: boolean;
    estado: 'despachado' | 'descartado' | null;
    despachado_por: string | null;
    despachado_at: string | null;
    motivo_descarte: string | null;
    aplicado_inicio_at: string | null;
    aplicado_fin_at: string | null;
    aplicado_por: string | null;
  }
  const [boomActivo, setBoomActivo] = useState<number>(1);
  const [boomsProgreso, setBoomsProgreso] = useState<BoomDesp[]>([]);
  const [modalDescartar, setModalDescartar] = useState<{ motivo: string } | null>(null);
  const [productosFlash, setProductosFlash] = useState<Set<number>>(new Set());
  const [productosRechazadosFlash, setProductosRechazadosFlash] = useState<Set<number>>(new Set());

  // modal ajuste
  const [modalAjuste, setModalAjuste] = useState<{ idx: number } | null>(null);
  const [modalReversion, setModalReversion] = useState<{ idx: number } | null>(null);
  // Modal para solicitar reversión completa de la boleta al ingeniero
  const [modalSolicReversion, setModalSolicReversion] = useState<{ motivo: string } | null>(null);
  // Modal para solicitar cambio de producto al ingeniero
  // idx = índice del producto en `productos`, motivo = texto del bodeguero,
  // sugerido = artículo sugerido (opcional, el ingeniero puede elegir otro).
  const [modalCambioProd, setModalCambioProd] = useState<{ productoId: number; motivo: string; sugerido: string } | null>(null);
  const [modalAgregarProd, setModalAgregarProd] = useState<{ motivo: string; sugerido: string } | null>(null);
  const [modalExcluirProd, setModalExcluirProd] = useState<{ productoId: number; motivo: string } | null>(null);
  // Contador de solicitudes tipo='nuevo' pendientes/aprobadas-sin-aplicar para
  // mantener vivo el polling aunque ningún producto del listado esté pendiente.
  const [hayNuevoPendiente, setHayNuevoPendiente] = useState(false);
  // Saldos de inventario (solo visibles si user tiene perm_vob).
  const [puedeVerSaldos, setPuedeVerSaldos] = useState(false);
  interface SaldoLinea {
    boleta_producto_id: number;
    inv_producto_id: number | null;
    articulo: string;
    requerido: number;
    stock_actual: number;
    cantidad_comprometida: number;
    disponible_neto: number;
    tiene_saldo: boolean;
    sin_enlace: boolean;
    demanda_pendiente_otras?: number;
    boletas_pendientes_otras?: string[];
    tiene_saldo_considerando_pendientes?: boolean;
  }
  const [saldos, setSaldos] = useState<Map<number, SaldoLinea>>(new Map());
  const [razSelIdx, setRazSelIdx]     = useState(0);
  const [razDetalle, setRazDetalle]   = useState('');
  // Ingenieros (usuarios con perm_vob) para escoger destinatario de la solicitud
  interface IngenieroOpt {
    user_id: string;
    email: string | null;
    nombre: string;
    phone: string | null;
    rol_codigo: string | null;
    rol_nombre: string | null;
  }
  const [ingenieros, setIngenieros] = useState<IngenieroOpt[]>([]);
  const [ingenieroSel, setIngenieroSel] = useState<string>('');

  useEffect(() => {
    injectBodegaCSS();
    supabase.auth.getUser().then(async ({ data }) => {
      setUsuario((data.user?.user_metadata?.nombre as string) || (data.user?.email ?? '') || 'bodega');
      // Detectar si el user tiene perm_vob (ve saldos de inventario).
      // Super-usuarios (lista fija de emails) bypassean los roles.
      const SUPER_USER_EMAILS = new Set([
        'sistemasmya@hotmail.com',
        'marcomv30@gmail.com',
      ]);
      if (data.user?.id) {
        const email = String(data.user.email || '').toLowerCase();
        if (SUPER_USER_EMAILS.has(email)) {
          setPuedeVerSaldos(true);
        } else {
          const { data: rolData } = await supabase
            .from('app_usuario_roles')
            .select('rol:app_roles!inner(perm_vob)')
            .eq('user_id', data.user.id)
            .eq('empresa_id', empresaId)
            .eq('activo', true);
          setPuedeVerSaldos(Array.isArray(rolData) && rolData.some((r: any) => r?.rol?.perm_vob === true));
        }
      }
    });
    // Cargar ingenieros para el dropdown
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const resp = await fetch(buildApiUrl('/aplicaciones/ingenieros'), {
          headers: {
            'x-empresa-id': String(empresaId),
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
        });
        const j = await resp.json().catch(() => ({}));
        if (resp.ok && Array.isArray(j.ingenieros)) {
          setIngenieros(j.ingenieros);
          if (j.ingenieros.length > 0) setIngenieroSel(j.ingenieros[0].user_id);
        }
      } catch (e) {

      }
    })();
  }, []);

  interface ReporteResumen {
    boomero_nombre?: string | null;
    registrado_por?: string | null;
    registrado_at?: string | null;
    updated_at?: string | null;
    observaciones?: string | null;
    inicio_at?: string | null;
    inicio_lat?: number | null;
    inicio_lng?: number | null;
    inicio_precision?: number | null;
    fin_at?: string | null;
    fin_lat?: number | null;
    fin_lng?: number | null;
    fin_precision?: number | null;
  }
  const [reportePorBoleta, setReportePorBoleta] = useState<Map<number, ReporteResumen>>(new Map());
  // Map boleta_id → cantidad de booms cerrados por bodega (estado='despachado').
  // Se usa para clasificar la boleta en los tabs Pendiente/En Proceso por AVANCE,
  // no por estado global: una boleta con booms parciales aparece en ambos tabs.
  const [boomsDespCountPorBoleta, setBoomsDespCountPorBoleta] = useState<Map<number, number>>(new Map());
  // Progreso por boom de cada boleta (estado bodega + estado boomero), para que
  // el tab "En Proceso" muestre los mismos chips que ve el boomero, en lectura.
  interface BoomProg {
    boom_num: number;
    despachado: boolean;            // bodega lo cerró
    descartado: boolean;            // fracción descartada por bodega
    aplicadoInicio: boolean;        // boomero arrancó en campo
    aplicadoFin: boolean;           // boomero finalizó en campo
  }
  const [boomsProgPorBoleta, setBoomsProgPorBoleta] = useState<Map<number, Map<number, BoomProg>>>(new Map());

  // `silencioso=true` durante el polling para no mostrar "Cargando..." ni causar parpadeo
  const cargar = useCallback(async (opts?: { silencioso?: boolean }) => {
    const silencioso = !!opts?.silencioso;
    if (!silencioso) setLoading(true);
    const [{ data }, { data: reportes }, { data: boomsDesp }, { data: boomsAplic }] = await Promise.all([
      supabase
        .from('app_boletas')
        .select('*')
        .eq('empresa_id', empresaId)
        .in('estado', ['pendiente', 'en_proceso', 'aplicada'])
        .order('semana', { ascending: false })
        .order('lote_num'),
      supabase
        .from('app_boleta_campo')
        .select('boleta_id, boomero_nombre, registrado_por, registrado_at, updated_at, observaciones, inicio_at, inicio_lat, inicio_lng, inicio_precision, fin_at, fin_lat, fin_lng, fin_precision')
        .eq('empresa_id', empresaId),
      supabase
        .from('app_boleta_booms_despachados')
        .select('boleta_id, boom_num, estado')
        .eq('empresa_id', empresaId),
      supabase
        .from('app_boleta_booms_aplicados')
        .select('boleta_id, boom_num, inicio_at, fin_at')
        .eq('empresa_id', empresaId),
    ]);
    if (!silencioso) setLoading(false);
    // Construir progreso por boom de cada boleta (bodega + boomero)
    const progMap = new Map<number, Map<number, BoomProg>>();
    const getBoom = (bid: number, num: number): BoomProg => {
      let m = progMap.get(bid);
      if (!m) { m = new Map(); progMap.set(bid, m); }
      let bp = m.get(num);
      if (!bp) { bp = { boom_num: num, despachado: false, descartado: false, aplicadoInicio: false, aplicadoFin: false }; m.set(num, bp); }
      return bp;
    };
    const despCount = new Map<number, number>();
    (boomsDesp ?? []).forEach((d: any) => {
      const bid = Number(d.boleta_id);
      const bp = getBoom(bid, Number(d.boom_num));
      if (d.estado === 'despachado') { bp.despachado = true; despCount.set(bid, (despCount.get(bid) ?? 0) + 1); }
      else if (d.estado === 'descartado') { bp.descartado = true; }
    });
    (boomsAplic ?? []).forEach((a: any) => {
      const bid = Number(a.boleta_id);
      const bp = getBoom(bid, Number(a.boom_num));
      if (a.inicio_at) bp.aplicadoInicio = true;
      if (a.fin_at) bp.aplicadoFin = true;
    });
    setBoomsDespCountPorBoleta(despCount);
    setBoomsProgPorBoleta(progMap);
    setBoletas((prev) => {
      const next = (data ?? []) as AppBoleta[];
      // Si no cambió nada importante, devolver la misma ref (evita re-render)
      if (silencioso && prev.length === next.length
          && prev.every((b, i) => b.id === next[i].id && b.estado === next[i].estado
                                && b.numero === next[i].numero
                                && b.despachado_por === next[i].despachado_por
                                && b.despachado_at === next[i].despachado_at)) {
        return prev;
      }
      return next;
    });
    const map = new Map<number, ReporteResumen>();
    (reportes ?? []).forEach((r: any) => {
      map.set(Number(r.boleta_id), {
        boomero_nombre: r.boomero_nombre,
        registrado_por: r.registrado_por,
        registrado_at: r.registrado_at,
        updated_at: r.updated_at,
        observaciones: r.observaciones,
        inicio_at: r.inicio_at,
        inicio_lat: r.inicio_lat,
        inicio_lng: r.inicio_lng,
        inicio_precision: r.inicio_precision,
        fin_at: r.fin_at,
        fin_lat: r.fin_lat,
        fin_lng: r.fin_lng,
        fin_precision: r.fin_precision,
      });
    });
    setReportePorBoleta((prev) => {
      if (!silencioso) return map;
      // Comparar maps: si tienen mismo tamaño y mismos hash de timestamps, mantener ref
      if (prev.size === map.size) {
        let igual = true;
        for (const [k, v] of map.entries()) {
          const pv = prev.get(k);
          if (!pv || pv.updated_at !== v.updated_at || pv.fin_at !== v.fin_at || pv.inicio_at !== v.inicio_at) {
            igual = false;
            break;
          }
        }
        if (igual) return prev;
      }
      return map;
    });
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Equipos (booms) disponibles, ordenados por capacidad. Para el recálculo de tanda.
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('app_booms')
        .select('id, nombre, capacidad_l')
        .eq('empresa_id', empresaId)
        .eq('activo', true)
        .order('capacidad_l');
      const eqs = (data ?? []) as { id: number; nombre: string; capacidad_l: number }[];
      setEquipos(eqs);
      // Default: 6000 L si existe, si no la mayor capacidad.
      const def = eqs.find(e => e.capacidad_l === 6000)?.capacidad_l ?? (eqs.length ? eqs[eqs.length - 1].capacidad_l : 6000);
      setCapacidadEquipo(def);
    })();
  }, [empresaId]);

  // Marca qué boletas pendientes pertenecen a una tanda (fórmula repetida), para
  // mostrar el indicador en las tarjetas. Cálculo liviano (1 query de productos)
  // que corre cuando cambia el set de boletas pendientes, no en cada polling.
  const idsPendientesKey = boletas.filter(b => perteneceATabPura(b, 'pendiente', boomsDespCountPorBoleta)).map(b => b.id).join(',');
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const pendientes = boletas.filter(b => perteneceATabPura(b, 'pendiente', boomsDespCountPorBoleta));
      if (pendientes.length === 0) { setBoletasEnTanda(new Map()); return; }
      const { data: prods } = await supabase
        .from('app_boleta_productos')
        .select('boleta_id, articulo, dosis, es_agua, excluida')
        .eq('empresa_id', empresaId)
        .in('boleta_id', pendientes.map(b => Number(b.id)));
      if (cancelado) return;
      const porBoleta = new Map<number, any[]>();
      (prods ?? []).forEach((p: any) => {
        const arr = porBoleta.get(Number(p.boleta_id)) ?? [];
        arr.push(p); porBoleta.set(Number(p.boleta_id), arr);
      });
      // firma: día (fecha_plan) + ciclo + productos(articulo:dosis) no-agua.
      // El día es clave: una tanda es del MISMO día (igual que en abrirTandas).
      const firmaDe = (b: AppBoleta) => {
        const lista = (porBoleta.get(Number(b.id)) ?? []).filter(p => !p.es_agua && !p.excluida);
        if (lista.length === 0) return null;
        const fp = lista.map(p => `${p.articulo}:${p.dosis}`).sort().join('|');
        return `dia:${(b as any).fecha_plan ?? ''}#ciclo:${(b as any).ciclo_id ?? ''}#${fp}`;
      };
      const porFirma = new Map<string, number[]>();
      pendientes.forEach(b => {
        const f = firmaDe(b);
        if (!f) return;
        const arr = porFirma.get(f) ?? [];
        arr.push(Number(b.id)); porFirma.set(f, arr);
      });
      const marca = new Map<number, { firma: string; n: number }>();
      porFirma.forEach((ids, firma) => {
        if (ids.length >= 2) ids.forEach(id => marca.set(id, { firma, n: ids.length }));
      });
      setBoletasEnTanda(marca);
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsPendientesKey, empresaId]);

  // Detecta tandas: agrupa las boletas PENDIENTES visibles (según día filtrado)
  // por fórmula idéntica (mismo set de articulo:dosis no-agua + volumen de agua).
  // Carga los productos bajo demanda y consolida el total de cada producto.
  async function abrirTandas() {
    setVista('tandas');
    setTandasLoading(true);
    setTandasData(null);
    // Boletas candidatas: las pendientes del día filtrado (o todas si no hay filtro).
    const candidatas = boletas.filter(b =>
      perteneceATab(b, 'pendiente') &&
      (!filtroDia || String((b as any).fecha_plan || '') === filtroDia),
    );
    const ids = candidatas.map(b => Number(b.id));
    if (ids.length === 0) { setTandasData([]); setTandasLoading(false); return; }
    const { data: prods } = await supabase
      .from('app_boleta_productos')
      .select('boleta_id, articulo, nombre_producto, descripcion, dosis, medida, cantidad_aplicar, es_agua, excluida')
      .eq('empresa_id', empresaId)
      .in('boleta_id', ids);
    // Agrupar productos por boleta.
    const porBoleta = new Map<number, any[]>();
    (prods ?? []).forEach((p: any) => {
      const arr = porBoleta.get(Number(p.boleta_id)) ?? [];
      arr.push(p);
      porBoleta.set(Number(p.boleta_id), arr);
    });
    // Firma de fórmula por boleta: MISMO PLAN (ciclo) + mismos productos no-agua
    // (articulo:dosis ordenados) + volumen de agua. El ciclo es clave: dos lotes
    // pueden coincidir en productos+dosis por casualidad pero ser de planes
    // distintos → NO se mezclan juntos.
    const grupos = new Map<string, TandaGrupo>();
    candidatas.forEach(b => {
      const lista = porBoleta.get(Number(b.id)) ?? [];
      const noAgua = lista.filter(p => !p.es_agua && !p.excluida);
      const agua = lista.find(p => p.es_agua);
      if (noAgua.length === 0) return;
      const firmaProd = noAgua
        .map(p => `${p.articulo}:${p.dosis}`)
        .sort()
        .join('|');
      const cicloId = (b as any).ciclo_id ?? '';
      const firma = `ciclo:${cicloId}#${firmaProd}#agua:${agua?.dosis ?? 0}`;
      const bid = Number(b.id);
      let g = grupos.get(firma);
      if (!g) {
        g = {
          firma,
          cicloNombre: (b as any).ciclo_nombre || '',
          boletas: [],
          productos: noAgua
            .slice()
            .sort((a, b2) => String(a.articulo).localeCompare(String(b2.articulo)))
            .map(p => ({
              articulo: String(p.articulo),
              nombre: p.nombre_producto || p.descripcion || p.articulo,
              dosis: Number(p.dosis || 0),
              medida: p.medida || '',
              porBoleta: {} as Record<number, number>,
              total: 0,
            })),
          aguaPorBoleta: {} as Record<number, number>,
          aguaTotal: 0,
          aguaMedida: agua?.medida || 'LT',
          aguaFormula: Number(agua?.dosis || 0),
        };
        grupos.set(firma, g);
      }
      g.boletas.push({
        id: bid,
        numero: b.numero,
        lote_num: b.lote_num || '',
        etapa_nombre: b.etapa_nombre || '',
      });
      // Cantidad de cada producto para este lote + acumular el total.
      noAgua.forEach(p => {
        const tp = g!.productos.find(x => x.articulo === String(p.articulo) && x.dosis === Number(p.dosis || 0));
        if (tp) {
          const cant = Number(p.cantidad_aplicar || 0);
          tp.porBoleta[bid] = (tp.porBoleta[bid] ?? 0) + cant;
          tp.total += cant;
        }
      });
      const aguaCant = Number(agua?.cantidad_aplicar || 0);
      g.aguaPorBoleta[bid] = (g.aguaPorBoleta[bid] ?? 0) + aguaCant;
      g.aguaTotal += aguaCant;
    });
    // Ordenar boletas de cada tanda por número de lote (columnas estables).
    grupos.forEach(g => g.boletas.sort((a, b) => {
      const na = parseInt(a.lote_num, 10), nb = parseInt(b.lote_num, 10);
      return (Number.isNaN(na) || Number.isNaN(nb)) ? a.lote_num.localeCompare(b.lote_num) : na - nb;
    }));
    // Solo tandas reales (2+ boletas), ordenadas por cantidad de boletas desc.
    const tandas = Array.from(grupos.values())
      .filter(g => g.boletas.length >= 2)
      .sort((a, b) => b.boletas.length - a.boletas.length);
    setTandasData(tandas);
    // Pre-marcar el checklist de las tandas que ya están preparadas en BD.
    setTandaCheck(prev => {
      const s = new Set(prev);
      tandas.forEach(t => {
        const preparada = t.boletas.every(tb => {
          const real = boletas.find(b => Number(b.id) === tb.id);
          return !!(real as any)?.mezcla_preparada_at;
        });
        if (preparada) t.productos.forEach(p => s.add(`${t.firma}#${p.articulo}`));
      });
      return s;
    });
    setTandasLoading(false);
  }

  // Marca (o desmarca) la mezcla de una tanda como preparada en todas sus boletas.
  // NO cambia el estado de las boletas ni toca inventario: solo trazabilidad de que
  // la mezcla física está lista.
  async function marcarMezclaTanda(tanda: TandaGrupo, preparada: boolean) {
    setGuardando(true);
    try {
      const ids = tanda.boletas.map(b => b.id);
      const { error } = await supabase
        .from('app_boletas')
        .update({
          mezcla_preparada_at: preparada ? new Date().toISOString() : null,
          mezcla_preparada_por: preparada ? usuario : null,
        })
        .eq('empresa_id', empresaId)
        .in('id', ids);
      if (error) { showMsg('err', error.message); return; }
      showMsg('ok', preparada
        ? `Mezcla "${tanda.cicloNombre}" marcada como preparada (${ids.length} boletas).`
        : `Mezcla "${tanda.cicloNombre}" desmarcada.`);
      await cargar({ silencioso: true });
      // Reflejar en la vista de tandas sin recargar todo.
      setTandasData(prev => prev ? prev.map(t => t.firma === tanda.firma
        ? { ...t, boletas: t.boletas } : t) : prev);
    } finally {
      setGuardando(false);
    }
  }

  // Al entrar a la lista (incluyendo cuando se vuelve desde despacho/confirmacion/vob),
  // recargar boletas y reportes. Polling silencioso cada 15s para detectar cambios
  // del boomero sin causar parpadeo visual.
  useEffect(() => {
    if (vista !== 'lista') return;
    cargar();
    const timer = window.setInterval(() => { cargar({ silencioso: true }); }, 15000);
    return () => window.clearInterval(timer);
  }, [vista, cargar]);

  function showMsg(tipo: 'ok' | 'err', texto: string) {
    setMsg({ tipo, texto });
    window.setTimeout(() => setMsg(null), tipo === 'err' ? 20000 : 8000);
  }

  function renderMsgBanner() {
    if (!msg) return null;
    return createPortal(
      <div style={{
        position: 'fixed',
        top: '16px',
        right: '16px',
        zIndex: 10000,
        maxWidth: 'min(90vw, 420px)',
        padding: '14px 16px',
        borderRadius: '12px',
        fontWeight: 700,
        fontSize: '15px',
        background: msg.tipo === 'ok' ? '#0f2c20' : '#34181c',
        border: `1px solid ${msg.tipo === 'ok' ? '#1d6e4f' : '#7d2f3a'}`,
        color: msg.tipo === 'ok' ? '#9df4c7' : '#ffb3bb',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
        animation: 'bod-toast-in 0.25s ease-out',
      }}>
        <span style={{ fontSize: '20px', lineHeight: 1, flexShrink: 0 }}>
          {msg.tipo === 'ok' ? '✓' : '⚠'}
        </span>
        <span style={{ flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.texto}</span>
        <button
          type="button"
          onClick={() => setMsg(null)}
          style={{
            border: 'none',
            borderRadius: '6px',
            background: 'var(--surface-overlay)',
            color: 'inherit',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 800,
            lineHeight: 1,
            padding: '4px 8px',
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>,
      document.body,
    );
  }

  // ── Abrir boleta para despacho ────────────────────────────────────────────

  async function abrirDespacho(b: AppBoleta) {
    setBoletaSel(b);
    // Cambiar de vista YA (antes de los awaits) para que la lista no se reemplace
    // por "Cargando..." y no haya parpadeo al tocar la tarjeta. La vista destino
    // se sabe desde b.estado.
    const aplicadaTotal = ['aplicada', 'archivada'].includes(String(b.estado));
    setVista(aplicadaTotal ? 'vob' : 'despacho');
    const { data } = await supabase
      .from('app_boleta_productos')
      .select('*')
      .eq('boleta_id', b.id)
      .eq('empresa_id', empresaId)
      .order('orden');
    const { data: solicitudes } = await supabase
      .from('app_boleta_ajuste_solicitudes')
      .select('*')
      .eq('boleta_id', b.id)
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false });
    const solPorProducto = new Map<number, any>();
    (solicitudes ?? []).forEach((s: any) => {
      if (!solPorProducto.has(Number(s.boleta_producto_id))) solPorProducto.set(Number(s.boleta_producto_id), s);
    });
    const nBoomsCompletos = Number(b.n_booms_completos ?? 0);
    const prods: ProdConAjuste[] = ((data ?? []) as AppBoletaProducto[]).map(p => {
      const solicitud = solPorProducto.get(Number(p.id));
      const estadoSolicitud = String(solicitud?.estado || '');
      const cantidadReal = estadoSolicitud === 'rechazada'
        ? Number(p.cantidad_aplicar ?? 0)
        : Number(solicitud?.cantidad_aprobada ?? solicitud?.cantidad_solicitada ?? p.cantidad_aplicar ?? 0);
      const ajustado = Math.abs(cantidadReal - (p.cantidad_aplicar ?? 0)) > 0.001;
      // Si hubo ajuste aprobado, redistribuir boom/fracción proporcional al volumen original
      const dist = ajustado
        ? redistribuirBoomFraccion(
            cantidadReal,
            Number(p.cantidad_boom ?? 0),
            Number(p.cantidad_fraccion ?? 0),
            nBoomsCompletos,
          )
        : { cantidad_boom: Number(p.cantidad_boom ?? 0), cantidad_fraccion: Number(p.cantidad_fraccion ?? 0) };
      return {
      ...p,
      cantidad_real: cantidadReal,
      cantidad_raw: cantidadReal.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }),
      razon_codigo: solicitud?.razon_codigo || '',
      razon_detalle: solicitud?.razon_detalle || '',
      respuesta_comentario: solicitud?.respuesta_comentario || null,
      ajustado,
      solicitud_id: solicitud?.id,
      solicitud_estado: solicitud?.estado,
      solicitud_tipo: solicitud?.tipo,
      cantidad_aprobada: solicitud?.cantidad_aprobada,
      cantidad_boom_efectiva: dist.cantidad_boom,
      cantidad_fraccion_efectiva: dist.cantidad_fraccion,
    };
    });
    setProductos(prods);
    setCheckeados(new Set());

    // Cargar saldos de inventario (solo si user tiene perm_vob)
    if (puedeVerSaldos) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${b.id}/saldos`), {
          headers: {
            'x-empresa-id': String(empresaId),
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
        });
        const j = await resp.json().catch(() => ({}));
        if (resp.ok && Array.isArray(j.productos)) {
          const m = new Map<number, SaldoLinea>();
          j.productos.forEach((s: SaldoLinea) => m.set(s.boleta_producto_id, s));
          setSaldos(m);
        } else {
          setSaldos(new Map());
        }
      } catch {
        setSaldos(new Map());
      }
    } else {
      setSaldos(new Map());
    }

    // Cargar reporte de campo del boomero (solo relevante para confirmacion/vob)
    if (b.estado === 'en_proceso' || b.estado === 'aplicada') {
      const { data: rep } = await supabase
        .from('app_boleta_campo')
        .select('id, registrado_por, updated_at, fin_at, inicio_at, inicio_lat, inicio_lng, fin_lat, fin_lng')
        .eq('boleta_id', b.id)
        .eq('empresa_id', empresaId)
        .maybeSingle();
      setReporteCampo(rep ? (rep as any) : null);
    } else {
      setReporteCampo(null);
    }
    // Cargar geojson del lote (solo para V°B°)
    setLoteGeojson(null);
    setTracks([]);
    setAnchoBoom(null);
    if (b.estado === 'aplicada') {
      if (!b.lote_id) {

      } else {
        const { data: lote, error } = await supabase
          .from('emp_lotes')
          .select('geojson')
          .eq('id', b.lote_id)
          .maybeSingle();
        if (error) {

        } else if (!lote) {

        } else if (!lote.geojson) {

        } else {
          setLoteGeojson(lote.geojson);
        }
      }
      // Cargar tracks GPS del recorrido
      const { data: trackData } = await supabase
        .from('app_boleta_campo_track')
        .select('id, empresa_id, boleta_id, t, lat, lng, accuracy_m, speed_ms, heading_deg')
        .eq('boleta_id', b.id)
        .eq('empresa_id', empresaId)
        .order('t', { ascending: true });
      setTracks((trackData ?? []) as any);
      // Cargar ancho del boom (necesario para cobertura)
      if (b.boom_id) {
        const { data: boomData } = await supabase
          .from('app_booms')
          .select('ancho_m')
          .eq('id', b.boom_id)
          .maybeSingle();
        if (boomData?.ancho_m != null) setAnchoBoom(Number(boomData.ancho_m));
      }
    }
    // Cargar progreso de booms despachados (Fase 1: despacho por boom)
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${b.id}/booms-despachados`), {
        headers: {
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
      });
      const j = await resp.json().catch(() => ({}));
      if (resp.ok && Array.isArray(j.booms)) {
        setBoomsProgreso(j.booms);
        // boom activo = primero NO despachado, o el último si todos están.
        // Bodega prepara sin esperar al boomero, así que el activo es siempre
        // el siguiente que falta por cerrar.
        const siguiente = j.booms.find((x: BoomDesp) => !x.despachado);
        if (siguiente) {
          setBoomActivo(siguiente.boom_num);
        } else if (j.booms.length > 0) {
          setBoomActivo(j.booms[j.booms.length - 1].boom_num);
        }
      } else {
        setBoomsProgreso([]);
        setBoomActivo(1);
      }
    } catch {
      setBoomsProgreso([]);
      setBoomActivo(1);
    }

    // La vista ya se fijó al inicio (según b.estado) para evitar parpadeo.
  }

  // ── Modal ajuste ──────────────────────────────────────────────────────────

  const refrescarSolicitudesBoleta = useCallback(async () => {
    if (!boletaSel) return;
    const { data: solicitudes, error } = await supabase
      .from('app_boleta_ajuste_solicitudes')
      .select('*')
      .eq('boleta_id', boletaSel.id)
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false });
    if (error) {

      return;
    }
    const solPorProducto = new Map<number, any>();
    (solicitudes ?? []).forEach((s: any) => {
      if (!solPorProducto.has(Number(s.boleta_producto_id))) solPorProducto.set(Number(s.boleta_producto_id), s);
    });

    // Mantener polling vivo mientras existan solicitudes 'nuevo' o 'excluir' pendientes
    setHayNuevoPendiente((solicitudes ?? []).some((s: any) =>
      (s.tipo === 'nuevo' || s.tipo === 'excluir') && s.estado === 'pendiente'
    ));

    // Si hay solicitudes 'nuevo' aprobadas con producto_id que aún no existe
    // en el listado, o solicitudes 'excluir' aprobadas que aún no se reflejan
    // en p.excluida, recargar TODOS los productos de la boleta.
    setProductos(prev => {
      const idsActuales = new Set(prev.map(p => Number(p.id)));
      const hayNuevoAprobado = (solicitudes ?? []).some((s: any) =>
        s.tipo === 'nuevo'
        && s.estado === 'aprobada'
        && s.boleta_producto_id
        && !idsActuales.has(Number(s.boleta_producto_id))
      );
      const hayExcluirAprobado = (solicitudes ?? []).some((s: any) => {
        if (s.tipo !== 'excluir' || s.estado !== 'aprobada') return false;
        const prod = prev.find(p => Number(p.id) === Number(s.boleta_producto_id));
        return prod && !prod.excluida;
      });
      if (hayNuevoAprobado || hayExcluirAprobado) {
        // Disparar recarga completa (async, no podemos await dentro del setState)
        supabase
          .from('app_boleta_productos').select('*')
          .eq('boleta_id', boletaSel.id).eq('empresa_id', empresaId)
          .order('orden')
          .then(({ data }) => {
            if (!data) return;
            const nBooms = Number(boletaSel?.n_booms_completos ?? 0);
            const nuevos: ProdConAjuste[] = (data as AppBoletaProducto[]).map(pp => {
              const sol = solPorProducto.get(Number(pp.id));
              const estado = String(sol?.estado || '');
              const cantidadReal = estado === 'rechazada'
                ? Number(pp.cantidad_aplicar ?? 0)
                : Number(sol?.cantidad_aprobada ?? sol?.cantidad_solicitada ?? pp.cantidad_aplicar ?? 0);
              const ajustado = Math.abs(cantidadReal - (pp.cantidad_aplicar ?? 0)) > 0.001;
              const dist = ajustado
                ? redistribuirBoomFraccion(cantidadReal, Number(pp.cantidad_boom ?? 0), Number(pp.cantidad_fraccion ?? 0), nBooms)
                : { cantidad_boom: Number(pp.cantidad_boom ?? 0), cantidad_fraccion: Number(pp.cantidad_fraccion ?? 0) };
              return {
                ...pp,
                cantidad_real: cantidadReal,
                cantidad_raw: cantidadReal.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }),
                razon_codigo: sol?.razon_codigo || '',
                razon_detalle: sol?.razon_detalle || '',
                respuesta_comentario: sol?.respuesta_comentario || null,
                ajustado,
                solicitud_id: sol?.id,
                solicitud_estado: sol?.estado,
                solicitud_tipo: sol?.tipo,
                cantidad_aprobada: sol?.cantidad_aprobada,
                cantidad_boom_efectiva: dist.cantidad_boom,
                cantidad_fraccion_efectiva: dist.cantidad_fraccion,
              };
            });
            setProductos(nuevos);
            // Flash al producto recién agregado
            (solicitudes ?? []).forEach((s: any) => {
              if (s.tipo === 'nuevo' && s.estado === 'aprobada' && s.boleta_producto_id && !idsActuales.has(Number(s.boleta_producto_id))) {
                setProductosFlash(curr => new Set(curr).add(Number(s.boleta_producto_id)));
                window.setTimeout(() => {
                  setProductosFlash(curr => { const n = new Set(curr); n.delete(Number(s.boleta_producto_id)); return n; });
                }, 6500);
              }
            });
          });
      }
      return prev.map(p => {
      const solicitud = solPorProducto.get(Number(p.id));
      if (!solicitud) return p;
      const estadoAnterior = String(p.solicitud_estado || '');
      const estadoNuevo = String(solicitud.estado || '');
      const cantidadReal = estadoNuevo === 'rechazada'
        ? Number(p.cantidad_aplicar ?? 0)
        : Number(solicitud.cantidad_aprobada ?? solicitud.cantidad_solicitada ?? p.cantidad_real);
      const pasoAAprobado = estadoAnterior === 'pendiente' && ['aprobada', 'modificada'].includes(estadoNuevo);
      const pasoARechazado = estadoAnterior === 'pendiente' && estadoNuevo === 'rechazada';
      if (pasoAAprobado || pasoARechazado) {
        const setFlash = pasoAAprobado ? setProductosFlash : setProductosRechazadosFlash;
        setFlash(current => new Set(current).add(Number(p.id)));
        window.setTimeout(() => {
          setFlash(current => {
            const next = new Set(current);
            next.delete(Number(p.id));
            return next;
          });
        }, 6500);
      }
      const ajustado = Math.abs(cantidadReal - (p.cantidad_aplicar ?? 0)) > 0.001;
      const nBooms = Number(boletaSel?.n_booms_completos ?? 0);
      const dist = ajustado
        ? redistribuirBoomFraccion(
            cantidadReal,
            Number(p.cantidad_boom ?? 0),
            Number(p.cantidad_fraccion ?? 0),
            nBooms,
          )
        : { cantidad_boom: Number(p.cantidad_boom ?? 0), cantidad_fraccion: Number(p.cantidad_fraccion ?? 0) };
      return {
        ...p,
        cantidad_real: cantidadReal,
        cantidad_raw: cantidadReal.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }),
        razon_codigo: solicitud.razon_codigo || p.razon_codigo,
        razon_detalle: solicitud.razon_detalle || p.razon_detalle,
        respuesta_comentario: solicitud.respuesta_comentario || null,
        ajustado,
        solicitud_id: solicitud.id,
        solicitud_estado: solicitud.estado,
        solicitud_tipo: solicitud.tipo,
        cantidad_aprobada: solicitud.cantidad_aprobada,
        cantidad_boom_efectiva: dist.cantidad_boom,
        cantidad_fraccion_efectiva: dist.cantidad_fraccion,
      };
      });
    });
  }, [boletaSel, empresaId]);

  useEffect(() => {
    if (!boletaSel || !['despacho', 'confirmacion'].includes(vista)) return;
    const hayPendientes = productos.some(p => p.solicitud_estado === 'pendiente');
    if (!hayPendientes && !hayNuevoPendiente) return;
    const timer = window.setInterval(() => {
      refrescarSolicitudesBoleta();
    }, 8000);
    return () => window.clearInterval(timer);
  }, [boletaSel, vista, productos, hayNuevoPendiente, refrescarSolicitudesBoleta]);

  // Polling reporte de campo: cuando bodega esta en 'confirmacion' o 'vob' y aun no hay reporte,
  // refresca cada 10s para detectar cuando el boomero lo carga o cuando finaliza.
  // Sigue refrescando hasta que la aplicación esté FINALIZADA (fin_at).
  useEffect(() => {
    if (!boletaSel || !['confirmacion', 'vob'].includes(vista) || reporteCampo?.fin_at) return;
    const timer = window.setInterval(async () => {
      const { data } = await supabase
        .from('app_boleta_campo')
        .select('id, registrado_por, updated_at, fin_at')
        .eq('boleta_id', boletaSel.id)
        .eq('empresa_id', empresaId)
        .maybeSingle();
      if (data) setReporteCampo(data as any);
    }, 10000);
    return () => window.clearInterval(timer);
  }, [boletaSel, vista, reporteCampo?.fin_at, empresaId]);

  // Polling progreso de booms (lado boomero): cuando bodega está en vista despacho,
  // refresca cada 12s para detectar cuando el boomero finalizó el boom actual
  // en campo y poder habilitar el siguiente cierre.
  useEffect(() => {
    if (!boletaSel || vista !== 'despacho') return;
    // Si no hay booms activos pendientes, no tiene sentido pollear
    const hayPendienteBoomero = boomsProgreso.some(b =>
      b.despachado && b.estado === 'despachado' && !b.aplicado_fin_at
    );
    if (!hayPendienteBoomero) return;
    const timer = window.setInterval(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/booms-despachados`), {
          headers: {
            'x-empresa-id': String(empresaId),
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
        });
        const j = await resp.json().catch(() => ({}));
        if (resp.ok && Array.isArray(j.booms)) {
          setBoomsProgreso(j.booms);
          // Si la boleta cambió de estado (p.ej. boomero finalizó el último → aplicada),
          // recargamos lista y salimos.
          if (j.boleta?.estado && j.boleta.estado !== boletaSel.estado) {
            await cargar();
          }
        }
      } catch { /* silencioso */ }
    }, 12000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boletaSel, vista, boomsProgreso, empresaId]);

  function abrirModalAjuste(idx: number) {
    const p = productos[idx];
    const razIdx = RAZONES_AJUSTE.findIndex(r => r.codigo === p.razon_codigo);
    setRazSelIdx(razIdx >= 0 ? razIdx : 0);
    setRazDetalle(p.razon_detalle);
    setModalAjuste({ idx });
  }

  function confirmarAjuste() {
    if (!modalAjuste) return;
    const raz = RAZONES_AJUSTE[razSelIdx];
    if (raz.codigo === 'OTRO' && !razDetalle.trim()) return;
    setProductos(prev => prev.map((p, i) => i === modalAjuste.idx
      ? { ...p, razon_codigo: raz.codigo, razon_detalle: razDetalle, ajustado: true }
      : p
    ));
    setModalAjuste(null);
    setRazDetalle('');
  }

  // Anula una solicitud PENDIENTE (enviada por error o sin respuesta). La línea
  // vuelve a su estado original (cantidad/producto planificado) y queda lista
  // para despachar o volver a solicitar.
  async function anularSolicitud(idx: number) {
    if (!boletaSel) return;
    const p = productos[idx];
    if (!p.solicitud_id || p.solicitud_estado !== 'pendiente') return;
    setGuardando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/ajuste-solicitudes/${p.solicitud_id}/cancelar`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        showMsg('err', payload?.error || 'No se pudo anular la solicitud.');
        return;
      }
      // Línea vuelve a su estado original
      setProductos(prev => prev.map((pp, i) => i === idx
        ? { ...pp, solicitud_id: undefined, solicitud_estado: undefined, ajustado: false, razon_codigo: '', razon_detalle: '' }
        : pp
      ));
      await refrescarSolicitudesBoleta();
      showMsg('ok', 'Solicitud anulada. La línea volvió a su estado original.');
    } catch (error: any) {
      showMsg('err', error?.message || 'No se pudo anular la solicitud.');
    } finally {
      setGuardando(false);
    }
  }

  async function solicitarAjuste(idx: number) {
    if (!boletaSel) return;
    const p = productos[idx];
    if (!p.ajustado) return;
    if (!p.razon_codigo) {
      abrirModalAjuste(idx);
      return;
    }
    if (p.solicitud_estado === 'pendiente') {
      showMsg('ok', 'Reenviando solicitud pendiente al ingeniero...');
    }
    if (p.solicitud_estado === 'aprobada' || p.solicitud_estado === 'modificada') {
      showMsg('ok', 'El ajuste ya fue aprobado. Puede continuar despacho.');
      return;
    }
    setGuardando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/ajuste-solicitudes`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          boleta_producto_id: p.id,
          cantidad_solicitada: p.cantidad_real,
          razon_codigo: p.razon_codigo,
          razon_detalle: p.razon_detalle,
          ingeniero_user_id: ingenieroSel || undefined,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detalle = payload?.details || payload?.hint || payload?.code || '';
        const msg = payload?.error || payload?.message || `HTTP ${resp.status}`;
        throw new Error(detalle ? `${msg} (${detalle})` : msg);
      }
      setProductos(prev => prev.map((pp, i) => i === idx
        ? { ...pp, solicitud_id: payload.solicitud?.id, solicitud_estado: 'pendiente' }
        : pp
      ));
      showMsg('ok', 'Solicitud enviada al ingeniero.');
    } catch (error: any) {

      showMsg('err', error?.message || 'No se pudo enviar la solicitud.');
    } finally {
      setGuardando(false);
    }
  }

  // ── Guardar despacho → en_proceso ─────────────────────────────────────────

  async function solicitarReversionAjuste(idx: number) {
    if (!boletaSel) return;
    if (vista !== 'despacho') {
      showMsg('err', 'La aprobacion ya no se puede revertir despues de confirmar el despacho.');
      return;
    }
    setModalReversion({ idx });
  }

  async function confirmarReversionAjuste() {
    if (!boletaSel || !modalReversion) return;
    const idx = modalReversion.idx;
    const p = productos[idx];
    const planificado = Number(p.cantidad_aplicar ?? 0);

    setModalReversion(null);
    setGuardando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/ajuste-solicitudes`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          boleta_producto_id: p.id,
          cantidad_solicitada: planificado,
          razon_codigo: 'OTRO',
          razon_detalle: `Reversion de aprobacion previa. Volver a cantidad planificada: ${fmtN(planificado)} ${p.medida || ''}.`,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detalle = payload?.details || payload?.hint || payload?.code || '';
        const msg = payload?.error || payload?.message || `HTTP ${resp.status}`;
        throw new Error(detalle ? `${msg} (${detalle})` : msg);
      }

      setProductos(prev => prev.map((pp, i) => i === idx
        ? {
            ...pp,
            cantidad_real: planificado,
            cantidad_raw: planificado.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }),
            ajustado: false,
            razon_codigo: 'OTRO',
            razon_detalle: 'Reversion solicitada al planificado.',
            solicitud_id: payload.solicitud?.id,
            solicitud_estado: 'pendiente',
            cantidad_aprobada: null,
          }
        : pp
      ));
      showMsg('ok', 'Solicitud de reversion enviada al ingeniero.');
    } catch (error: any) {

      showMsg('err', error?.message || 'No se pudo enviar la solicitud de reversion.');
    } finally {
      setGuardando(false);
    }
  }

  // ── Solicitar reversión 'en_proceso' → 'pendiente' al ingeniero ───────────
  // El bodeguero abre un modal para escribir el motivo. Al confirmar se envía
  // una solicitud al ingeniero por email/WhatsApp. Si el ingeniero aprueba
  // (desde el link público), el backend ejecuta la reversión: borra reporte de
  // campo, tracks GPS y booms aplicados, y vuelve la boleta a 'pendiente'.
  function abrirSolicReversion() {
    setModalSolicReversion({ motivo: '' });
  }

  async function confirmarSolicReversion() {
    if (!boletaSel || !modalSolicReversion) return;
    const motivoLimpio = modalSolicReversion.motivo.trim();
    if (!motivoLimpio) {
      showMsg('err', 'Debés indicar un motivo para la reversión.');
      return;
    }
    setGuardando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/ajuste-solicitudes`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          tipo: 'reversion',
          razon_codigo: 'REVERSION',
          razon_detalle: motivoLimpio,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detalle = payload?.details || payload?.hint || payload?.code || '';
        const msg = payload?.error || payload?.message || `HTTP ${resp.status}`;
        throw new Error(detalle ? `${msg} (${detalle})` : msg);
      }
      setModalSolicReversion(null);
      showMsg('ok', 'Solicitud de reversión enviada al ingeniero.');
    } catch (error: any) {

      showMsg('err', error?.message || 'No se pudo enviar la solicitud.');
    } finally {
      setGuardando(false);
    }
  }

  // ── Solicitar cambio de PRODUCTO al ingeniero ──────────────────────────────
  // El bodeguero abre un modal con motivo + opcional sugerido. Al confirmar
  // envía la solicitud (tipo='producto'). El ingeniero elegirá el sustituto
  // del plan de manejo al aprobar, y el backend reemplaza el producto.
  function abrirCambioProd(productoId: number) {
    setModalCambioProd({ productoId, motivo: '', sugerido: '' });
  }

  async function confirmarCambioProd() {
    if (!boletaSel || !modalCambioProd) return;
    const motivoLimpio = modalCambioProd.motivo.trim();
    if (!motivoLimpio) {
      showMsg('err', 'Debés indicar un motivo para el cambio.');
      return;
    }
    const p = productos.find(pp => pp.id === modalCambioProd.productoId);
    if (!p) return;
    setGuardando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/ajuste-solicitudes`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          tipo: 'producto',
          boleta_producto_id: p.id,
          razon_codigo: 'CAMBIO_PRODUCTO',
          razon_detalle: motivoLimpio,
          producto_sugerido_articulo: modalCambioProd.sugerido.trim() || null,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detalle = payload?.details || payload?.hint || payload?.code || '';
        const msg = payload?.error || payload?.message || `HTTP ${resp.status}`;
        throw new Error(detalle ? `${msg} (${detalle})` : msg);
      }
      setModalCambioProd(null);
      showMsg('ok', 'Solicitud de cambio enviada al ingeniero.');
    } catch (error: any) {
      showMsg('err', error?.message || 'No se pudo enviar la solicitud.');
    } finally {
      setGuardando(false);
    }
  }

  // Agregar producto nuevo (no reposición). El ingeniero elige cuál del plan
  // y la dosis al aprobar; el backend inserta la fila en app_boleta_productos.
  function abrirAgregarProd() {
    setModalAgregarProd({ motivo: '', sugerido: '' });
  }

  async function confirmarAgregarProd() {
    if (!boletaSel || !modalAgregarProd) return;
    const motivoLimpio = modalAgregarProd.motivo.trim();
    if (!motivoLimpio) {
      showMsg('err', 'Debés indicar un motivo para agregar el producto.');
      return;
    }
    setGuardando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/ajuste-solicitudes`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          tipo: 'nuevo',
          razon_codigo: 'AGREGAR_PRODUCTO',
          razon_detalle: motivoLimpio,
          producto_sugerido_articulo: modalAgregarProd.sugerido.trim() || null,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detalle = payload?.details || payload?.hint || payload?.code || '';
        const msg = payload?.error || payload?.message || `HTTP ${resp.status}`;
        throw new Error(detalle ? `${msg} (${detalle})` : msg);
      }
      setModalAgregarProd(null);
      setHayNuevoPendiente(true);
      showMsg('ok', 'Solicitud enviada al ingeniero.');
    } catch (error: any) {
      showMsg('err', error?.message || 'No se pudo enviar la solicitud.');
    } finally {
      setGuardando(false);
    }
  }

  // Excluir producto existente. Si el ingeniero aprueba, queda marcado como
  // excluida=true y el bodeguero no lo despacha.
  function abrirExcluirProd(productoId: number) {
    setModalExcluirProd({ productoId, motivo: '' });
  }

  async function confirmarExcluirProd() {
    if (!boletaSel || !modalExcluirProd) return;
    const motivoLimpio = modalExcluirProd.motivo.trim();
    if (!motivoLimpio) {
      showMsg('err', 'Debés indicar un motivo para excluir el producto.');
      return;
    }
    const p = productos.find(pp => pp.id === modalExcluirProd.productoId);
    if (!p) return;
    setGuardando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/ajuste-solicitudes`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          tipo: 'excluir',
          boleta_producto_id: p.id,
          razon_codigo: 'EXCLUIR_PRODUCTO',
          razon_detalle: motivoLimpio,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detalle = payload?.details || payload?.hint || payload?.code || '';
        const msg = payload?.error || payload?.message || `HTTP ${resp.status}`;
        throw new Error(detalle ? `${msg} (${detalle})` : msg);
      }
      setModalExcluirProd(null);
      setHayNuevoPendiente(true);
      showMsg('ok', 'Solicitud de exclusión enviada al ingeniero.');
    } catch (error: any) {
      showMsg('err', error?.message || 'No se pudo enviar la solicitud.');
    } finally {
      setGuardando(false);
    }
  }

  // ── Despacho boom por boom (Fase 1) ───────────────────────────────────────
  // Reemplaza el antiguo `guardarDespacho` (despacho atómico): ahora el
  // bodeguero cierra boom por boom desde el wizard, y el backend maneja la
  // transición de estado pendiente→en_proceso→aplicada.

  // Refresca el progreso de booms desde el backend.
  async function refrescarBooms(boletaId: number): Promise<BoomDesp[]> {
    const { data: { session } } = await supabase.auth.getSession();
    const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaId}/booms-despachados`), {
      headers: {
        'x-empresa-id': String(empresaId),
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
    });
    const j = await resp.json().catch(() => ({}));
    const booms: BoomDesp[] = (resp.ok && Array.isArray(j.booms)) ? j.booms : [];
    setBoomsProgreso(booms);
    return booms;
  }

  // Cierra el boom activo. El backend maneja la transición pendiente→en_proceso
  // al cerrar el primer boom, y →aplicada al cerrar el último.
  async function cerrarBoomActivo() {
    if (!boletaSel) return;
    const item = boomsProgreso.find(b => b.boom_num === boomActivo);
    if (!item) return;
    if (item.despachado) {
      showMsg('err', `Este boom ya fue cerrado.`);
      return;
    }
    // Validar verificación: todos los productos no excluidos deben estar checkeados
    const totalProds = productos.filter(p => !p.es_agua && !p.excluida).length;
    if (checkeados.size !== totalProds || totalProds === 0) {
      showMsg('err', `Verifique los ${totalProds - checkeados.size} producto(s) restantes antes de cerrar el boom.`);
      return;
    }
    // Validar ajustes pendientes (mismas reglas que guardarDespacho)
    const sinRazon = productos.filter(p => !p.es_agua && p.ajustado && !p.razon_codigo);
    if (sinRazon.length > 0) {
      showMsg('err', `Falta solicitar autorización para ${sinRazon.length} producto(s) ajustado(s).`);
      return;
    }
    const sinAprobar = productos.filter(p =>
      !p.es_agua && (
        p.solicitud_estado === 'pendiente' ||
        (p.ajustado && !['aprobada', 'modificada'].includes(String(p.solicitud_estado || '')))
      )
    );
    if (sinAprobar.length > 0) {
      showMsg('err', `Hay ${sinAprobar.length} ajuste(s) pendientes de aprobación del ingeniero.`);
      return;
    }

    setGuardando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/cerrar-boom`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          boom_num: item.boom_num,
          tipo: item.tipo,
          despachado_por: usuario,
        }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        showMsg('err', j?.error || 'No se pudo cerrar el boom.');
        return;
      }

      // Si era el primer boom cerrado y la boleta estaba pendiente, crear reservas de inventario.
      const eraPrimero = !boomsProgreso.some(b => b.despachado);
      if (eraPrimero) {
        try {
          await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/reservar`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-empresa-id': String(empresaId),
              ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
            },
          });
        } catch { /* best-effort */ }
      }

      // Refrescar progreso. Quedarse en el mismo boom (el boomero debe
      // finalizar en campo antes de habilitar el siguiente). La boleta NO
      // pasa a 'aplicada' aquí — eso ocurre cuando el boomero finaliza el
      // último boom (lado backend en finalizar-boom-aplicacion).
      await refrescarBooms(boletaSel.id);
      showMsg('ok', item.tipo === 'fraccion'
        ? `Fracción cerrada. Esperando a que el boomero la aplique en campo.`
        : `Boom ${item.boom_num} cerrado. Esperando a que el boomero lo aplique en campo.`);
    } finally {
      setGuardando(false);
    }
  }

  // Descarta la fracción (caso especial: mezcla mixta no reintegrable a bodega).
  async function descartarFraccion(motivo: string) {
    if (!boletaSel) return;
    if (!motivo.trim()) {
      showMsg('err', 'Debe indicar el motivo del descarte.');
      return;
    }
    setGuardando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/descartar-fraccion`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ motivo: motivo.trim(), despachado_por: usuario }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        showMsg('err', j?.error || 'No se pudo descartar la fracción.');
        return;
      }
      setModalDescartar(null);
      // El backend ya validó que el boomero finalizó todos los completos
      // y marcó la boleta como 'aplicada'. Aplicar inventario y volver a lista.
      showMsg('ok', `Fracción descartada — boleta aplicada.`);
      try {
        const cantidades: Record<number, number> = {};
        for (const p of productos) {
          if (!p.es_agua && !p.excluida) cantidades[p.id] = Number(p.cantidad_real ?? p.cantidad_aplicar ?? 0);
        }
        await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/aplicar-inventario`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-empresa-id': String(empresaId),
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({ cantidades }),
        });
      } catch { /* best-effort */ }
      await cargar();
      setVista('lista');
      setBoletaSel(null);
    } finally {
      setGuardando(false);
    }
  }

  // ── Confirmar aplicación → aplicada ───────────────────────────────────────

  // Confirmación rápida desde la lista: la cédula ya está completa (sin ajustes pendientes
  // y con reporte finalizado), así que se confirma directo sin abrir el detalle.
  async function confirmarAplicacionRapida(b: AppBoleta) {
    setGuardando(true);
    try {
      // Verificar que no haya ajustes pendientes (carga rápida)
      const { data: solicitudes } = await supabase
        .from('app_boleta_ajuste_solicitudes')
        .select('estado')
        .eq('boleta_id', b.id)
        .eq('empresa_id', empresaId);
      const pendientes = (solicitudes ?? []).filter((s: any) => s.estado === 'pendiente').length;
      if (pendientes > 0) {
        showMsg('err', `Esta boleta tiene ${pendientes} ajuste(s) pendientes de aprobación. Abrí el detalle para revisar.`);
        return;
      }
      const { error } = await supabase
        .from('app_boletas')
        .update({
          estado: 'aplicada',
          aplicado_por: usuario,
          aplicado_at: new Date().toISOString(),
          fecha_aplicacion: new Date().toISOString().slice(0, 10),
        })
        .eq('id', b.id);
      if (error) { showMsg('err', error.message); return; }

      // Convertir reservas en movimientos reales (sin overrides — usa cantidad_reservada)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        await fetch(buildApiUrl(`/aplicaciones/boletas/${b.id}/aplicar-inventario`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-empresa-id': String(empresaId),
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
        });
      } catch { /* best-effort */ }

      showMsg('ok', `Cédula cerrada — boleta ${b.numero}`);
      await cargar();
    } finally {
      setGuardando(false);
    }
  }

  async function confirmarAplicacion() {
    if (!boletaSel) return;
    // Bloquear si el boomero aun no lleno o no finalizo la aplicacion
    if (!reporteCampo?.id) {
      showMsg('err', 'No se puede confirmar: el boomero aun no ha llenado el reporte de campo.');
      return;
    }
    if (!reporteCampo?.fin_at) {
      showMsg('err', 'No se puede confirmar: el boomero aun no ha marcado "Finalizar aplicación".');
      return;
    }
    const sinRazon = productos.filter(p => !p.es_agua && p.ajustado && !p.razon_codigo);
    if (sinRazon.length > 0) {
      showMsg('err', `Falta solicitar autorizacion para ${sinRazon.length} producto(s) ajustado(s).`);
      return;
    }
    const sinAprobar = productos.filter(p =>
      !p.es_agua && (
        p.solicitud_estado === 'pendiente' ||
        (p.ajustado && !['aprobada', 'modificada'].includes(String(p.solicitud_estado || '')))
      )
    );
    if (sinAprobar.length > 0) {
      showMsg('err', `Hay ${sinAprobar.length} ajuste(s) pendientes de aprobacion del ingeniero.`);
      return;
    }
    setGuardando(true);

    // Registrar ajustes nuevos si los hay
    const ajustes: Omit<AppBoletaAjuste, 'id' | 'created_at'>[] = productos
      .filter(p => !p.es_agua && p.ajustado && p.razon_codigo)
      .map(p => ({
        empresa_id: empresaId,
        boleta_id: boletaSel.id,
        boleta_producto_id: p.id,
        articulo: p.articulo,
        nombre_producto: p.nombre_producto,
        cantidad_boleta: p.cantidad_aplicar,
        cantidad_real: p.cantidad_aprobada ?? p.cantidad_real,
        razon_codigo: p.razon_codigo,
        razon_detalle: p.razon_detalle || null,
        registrado_por: usuario,
      }));
    if (ajustes.length > 0) {
      const { error: eAj } = await supabase.from('app_boleta_ajustes').insert(ajustes);
      if (eAj) { showMsg('err', eAj.message); setGuardando(false); return; }
    }

    const { error: eBoleta } = await supabase
      .from('app_boletas')
      .update({
        estado: 'aplicada',
        aplicado_por: usuario,
        aplicado_at: new Date().toISOString(),
        fecha_aplicacion: new Date().toISOString().slice(0, 10),
      })
      .eq('id', boletaSel.id);
    if (eBoleta) { setGuardando(false); showMsg('err', eBoleta.message); return; }

    // Convertir reservas en movimientos reales de inventario (salida de bodega).
    // Las cantidades reales aplicadas vienen del estado actual de productos
    // (puede diferir del planificado por ajustes aprobados).
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const cantidades: Record<number, number> = {};
      for (const p of productos) {
        if (!p.es_agua && !p.excluida) cantidades[p.id] = Number(p.cantidad_real ?? p.cantidad_aplicar ?? 0);
      }
      await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/aplicar-inventario`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ cantidades }),
      });
    } catch { /* best-effort: la boleta ya quedó aplicada */ }

    setGuardando(false);
    showMsg('ok', 'Aplicación confirmada.');
    await cargar();
    setFiltroEstado('aplicada');
    setVista('lista');
    setBoletaSel(null);
  }

  // ── V°B° Ingeniero → archivada ────────────────────────────────────────────

  async function darVoBo() {
    if (!boletaSel) return;
    if (!reporteCampo?.id) {
      showMsg('err', 'No se puede dar V°B°: el boomero aún no ha llenado el reporte de campo.');
      return;
    }
    if (!reporteCampo?.fin_at) {
      showMsg('err', 'No se puede dar V°B°: el boomero aún no ha marcado "Finalizar aplicación".');
      return;
    }
    setGuardando(true);
    const { error } = await supabase
      .from('app_boletas')
      .update({ estado: 'archivada', vob_por: usuario, vob_at: new Date().toISOString() })
      .eq('id', boletaSel.id);
    setGuardando(false);
    if (error) { showMsg('err', error.message); return; }
    showMsg('ok', 'V°B° registrado — boleta archivada.');
    await cargar();
    setFiltroEstado('pendiente');
    setVista('lista');
    setBoletaSel(null);
  }

  // ── Render lista ──────────────────────────────────────────────────────────

  // Filtra por estado + texto de búsqueda (número de boleta, lote, finca,
  // etapa o bloques). Normaliza acentos/mayúsculas para que la búsqueda sea laxa.
  const COMBINING_MARKS = new RegExp('[\\u0300-\\u036f]', 'g');
  const norm = (s: unknown) =>
    String(s ?? '').toLowerCase().normalize('NFD').replace(COMBINING_MARKS, '');
  const q = norm(busqueda.trim());

  // Clasificación por AVANCE de booms (no por estado global). Una boleta puede
  // pertenecer a varios tabs a la vez:
  //  - 'aplicada': estado global aplicada/archivada.
  //  - 'pendiente': a bodega le falta cerrar al menos un boom.
  //  - 'en_proceso': bodega ya cerró al menos un boom (avance) y no está aplicada.
  const perteneceATab = (b: AppBoleta, tab: 'pendiente' | 'en_proceso' | 'aplicada'): boolean =>
    perteneceATabPura(b, tab, boomsDespCountPorBoleta);

  const coincideBusqueda = (b: AppBoleta) => {
    if (!q) return true;
    const campos = [b.numero, b.lote_num, b.finca_nombre, b.etapa_nombre, b.bloques, b.semana];
    return campos.some(c => norm(c).includes(q));
  };

  // Días de aplicación (fecha_plan) disponibles en el tab actual, ordenados.
  const diasDisponibles = Array.from(
    new Set(
      boletas
        .filter(b => perteneceATab(b, filtroEstado) && (b as any).fecha_plan)
        .map(b => String((b as any).fecha_plan)),
    ),
  ).sort();

  const coincideDia = (b: AppBoleta) => !filtroDia || String((b as any).fecha_plan || '') === filtroDia;

  // Conteo de boletas por día (en el tab actual) para mostrarlo en cada chip.
  const conteoPorDia = new Map<string, number>();
  boletas.filter(b => perteneceATab(b, filtroEstado)).forEach(b => {
    const d = String((b as any).fecha_plan || '');
    if (d) conteoPorDia.set(d, (conteoPorDia.get(d) ?? 0) + 1);
  });
  const totalDias = Array.from(conteoPorDia.values()).reduce((s, n) => s + n, 0);

  const boletasFiltradas = boletas.filter(b => perteneceATab(b, filtroEstado) && coincideBusqueda(b) && coincideDia(b));

  // ¿Hay tandas (fórmula repetida) en el día filtrado? Para mostrar el botón "Ver
  // tandas" y los chips de tanda solo cuando realmente hay mezclas conjuntas.
  const hayTandasEnDia = boletas.some(b =>
    perteneceATab(b, 'pendiente') &&
    (!filtroDia || String((b as any).fecha_plan || '') === filtroDia) &&
    boletasEnTanda.has(Number(b.id)),
  );

  // Subtítulo del navbar según la sub-vista (breadcrumb "Terminal Bodega › ...").
  // Tocar "Terminal Bodega" en el navbar regresa a la lista (sale del despacho).
  const subVista =
    vista === 'despacho' ? 'Preparar Despacho'
    : vista === 'confirmacion' ? 'Confirmar Aplicación'
    : vista === 'vob' ? 'Visto Bueno'
    : vista === 'tandas' ? 'Tandas de mezcla'
    : '';
  const volverALista = useCallback(() => { setVista('lista'); setBoletaSel(null); }, []);
  useNavbarSubtitulo(subVista, vista !== 'lista', volverALista);

  if (vista === 'lista') {
    return (
      <div className="bod-lista" style={{ minHeight: '100%', background: 'var(--surface-base)' }}>
        <ConexionBanner />
        <div style={{ padding: '12px' }}>

        {renderMsgBanner()}

        {/* Header con boton refresh */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '8px' }}>
          <div style={{ fontSize: '13px', color: 'var(--ink-faint)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
            Boletas · {boletasFiltradas.length}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Ver tandas de mezcla: solo si hay fórmulas repetidas en el día filtrado */}
            {filtroEstado === 'pendiente' && hayTandasEnDia && (
              <button onClick={abrirTandas} style={{
                minHeight: '48px', padding: '0 16px', borderRadius: '12px', fontSize: '14px', fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap',
                border: '1px solid var(--emp-accent)', background: 'var(--emp-accent-bg)',
                color: 'var(--emp-accent-txt)', cursor: 'pointer',
              }}>🧪 Ver tandas</button>
            )}
            <button onClick={cargar} aria-label="Actualizar" style={{
              minWidth: '48px', minHeight: '48px', padding: '0 14px', borderRadius: '12px', fontSize: '20px',
              border: '1px solid var(--line)', background: 'var(--surface-overlay)',
              color: 'var(--ink-muted)', cursor: 'pointer', fontWeight: 700,
            }}>↺</button>
          </div>
        </div>

        {/* Buscador: filtra por número de boleta, lote, finca, etapa o bloques */}
        <div style={{ position: 'relative', marginBottom: '12px' }}>
          <span style={{
            position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
            fontSize: '16px', color: 'var(--ink-faint)', pointerEvents: 'none',
          }}>🔍</span>
          <input
            type="text"
            inputMode="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar por número, lote, finca…"
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '12px 40px 12px 40px', borderRadius: '12px', fontSize: '15px',
              border: '1px solid var(--line)', background: 'var(--surface-raised)',
              color: 'var(--ink)', outline: 'none',
            }}
          />
          {busqueda && (
            <button
              onClick={() => setBusqueda('')}
              aria-label="Limpiar búsqueda"
              style={{
                position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                width: '32px', height: '32px', borderRadius: '8px',
                border: 'none', background: 'transparent', color: 'var(--ink-muted)',
                cursor: 'pointer', fontSize: '18px', lineHeight: 1,
              }}
            >✕</button>
          )}
        </div>

        {/* Tabs de estado: grid 3 columnas equitativas para movil */}
        <div className="bod-tabs" style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginBottom: '16px',
        }}>
          {(['pendiente', 'en_proceso', 'aplicada'] as const).map(est => {
            const col = estadoColor(est);
            const activo = filtroEstado === est;
            const cnt = boletas.filter(b => perteneceATab(b, est)).length;
            return (
              <button key={est} onClick={() => { setFiltroEstado(est); setFiltroDia(''); }} style={{
                minHeight: '52px', padding: '8px 4px', borderRadius: '10px',
                cursor: 'pointer', border: `2px solid ${activo ? col.border : 'rgba(255,255,255,0.1)'}`,
                background: activo ? col.bg : 'transparent',
                color: activo ? col.text : 'var(--ink-muted)',
                transition: 'all 0.15s',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px',
              }}>
                <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {estadoLabel(est)}
                </span>
                <span style={{
                  background: activo ? col.border : 'rgba(255,255,255,0.1)',
                  color: '#fff', borderRadius: '999px', padding: '1px 10px', fontSize: '14px', fontWeight: 800, minWidth: '28px',
                }}>{cnt}</span>
              </button>
            );
          })}
        </div>

        {/* Filtro por día de aplicación (fecha_plan). Chips horizontales táctiles. */}
        {diasDisponibles.length > 1 && (
          <div style={{
            display: 'flex', gap: '6px', marginBottom: '16px',
            overflowX: 'auto', paddingBottom: '2px', WebkitOverflowScrolling: 'touch',
          }}>
            {/* Chips de día con conteo. Chip = paleta (accent); el contador = naranja sólido. */}
            {([{ key: '', label: 'Todos los días', cnt: totalDias }, ...diasDisponibles.map(d => ({ key: d, label: fmtFecha(d), cnt: conteoPorDia.get(d) ?? 0 }))]).map(({ key, label, cnt }) => {
              const activo = filtroDia === key;
              return (
                <button
                  key={key || 'todos'}
                  onClick={() => setFiltroDia(key)}
                  onMouseEnter={(e) => { if (!activo) { e.currentTarget.style.background = 'var(--emp-accent-bg)'; e.currentTarget.style.borderColor = 'var(--emp-accent)'; e.currentTarget.style.color = 'var(--emp-accent-txt)'; } }}
                  onMouseLeave={(e) => { if (!activo) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--line)'; e.currentTarget.style.color = 'var(--ink-muted)'; } }}
                  style={{
                    flexShrink: 0, minHeight: '40px', padding: '6px 12px', borderRadius: '999px',
                    cursor: 'pointer', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap',
                    display: 'flex', alignItems: 'center', gap: '7px',
                    transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                    border: `1.5px solid ${activo ? 'var(--emp-accent)' : 'var(--line)'}`,
                    background: activo ? 'var(--emp-accent-bg)' : 'transparent',
                    color: activo ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                  }}
                >
                  {label}
                  <span style={{
                    background: '#f97316', color: '#fff',
                    borderRadius: '999px', padding: '1px 8px', fontSize: '12px', fontWeight: 800, minWidth: '22px', textAlign: 'center',
                  }}>{cnt}</span>
                </button>
              );
            })}
          </div>
        )}

        {loading && <p style={{ color: 'var(--ink-muted)', textAlign: 'center', padding: '40px' }}>Cargando...</p>}

        {!loading && boletasFiltradas.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '60px 20px',
            color: 'var(--ink-muted)', fontSize: '15px',
          }}>
            {q
              ? <>Sin resultados para <strong>"{busqueda}"</strong> en estado <strong>{estadoLabel(filtroEstado)}</strong></>
              : <>No hay boletas en estado <strong>{estadoLabel(filtroEstado)}</strong></>}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {boletasFiltradas.map(b => {
            const col = estadoColor(b.estado);
            const repRapido = reportePorBoleta.get(Number(b.id));
            const cedulaLista = b.estado === 'en_proceso' && !!repRapido?.fin_at;
            // Avance de booms despachados por bodega (para mostrar progreso parcial)
            const totalBoomsB = Number(b.n_booms_completos || 0) + (Number(b.litros_fraccion || 0) > 0 ? 1 : 0);
            const cerradosB = boomsDespCountPorBoleta.get(Number(b.id)) ?? 0;
            const despachoParcial = cerradosB > 0 && cerradosB < totalBoomsB;
            // Color custom para el chip cuando la cédula está lista (azul = "proceso terminado")
            const chipBorder = cedulaLista ? '#3b82f6' : col.border;
            const chipColor  = cedulaLista ? 'var(--tone-info-text)' : col.text;
            const chipLabel  = cedulaLista ? 'PROCESO TERMINADO' : estadoLabel(b.estado);
            // El tab "En Proceso" es SOLO INFORMATIVO para bodega: muestra el
            // avance de booms ya despachados (lo que tiene el boomero) sin permitir
            // abrir/editar. La preparación se hace siempre desde el tab Pendiente.
            // Excepción: si la cédula está lista para cerrar (cedulaLista), sí
            // permitimos la confirmación rápida.
            const soloInfo = filtroEstado === 'en_proceso' && !cedulaLista;
            // Color de la tarjeta: en modo solo-info usar el mismo tono neutro/ámbar
            // del Terminal Boomero (no el celeste de pendiente), para que el
            // bodeguero distinga visualmente que es monitoreo de campo.
            const cardBorder = soloInfo ? 'rgba(245,158,11,0.4)' : col.border;
            const cardBg     = soloInfo ? 'rgba(245,158,11,0.07)' : col.bg;
            // Marca persistente de boleta corregida/revisada manualmente (bandera en BD)
            // o con dato anómalo actual (volumen 0 / sin booms). El triángulo queda
            // aunque ya se haya corregido, para trazabilidad de la intervención.
            const datoAnomalo = b.estado === 'pendiente'
              && Number(b.n_booms_completos || 0) === 0
              && Number(b.litros_fraccion || 0) === 0;
            const requiereRevision = !!(b as any).revisada_manual || datoAnomalo;
            return (
              <button
                key={b.id}
                disabled={guardando || soloInfo}
                onClick={() => {
                  if (soloInfo) return;
                  // Si la cédula está completa, confirmar directamente sin abrir detalle
                  if (cedulaLista) {
                    confirmarAplicacionRapida(b);
                  } else {
                    abrirDespacho(b);
                  }
                }}
                style={{
                  position: 'relative', overflow: 'hidden',
                  width: '100%', textAlign: 'left', padding: '14px 14px 12px', borderRadius: '14px',
                  border: `1.5px solid ${cardBorder}`, background: cardBg,
                  cursor: guardando ? 'wait' : soloInfo ? 'default' : 'pointer', transition: 'opacity 0.15s',
                  minHeight: '110px',
                  opacity: guardando ? 0.6 : 1,
                }}>
                {/* Esquina amarilla: boleta corregida manualmente o con dato anómalo */}
                {requiereRevision && (
                  <div
                    title={(b as any).revisada_manual ? ((b as any).revisada_manual_nota || 'Boleta revisada/corregida manualmente') : 'Volumen 0 / sin booms — requiere revisión'}
                    style={{
                      position: 'absolute', top: 0, right: 0, width: 0, height: 0,
                      borderTop: '40px solid #facc15', borderLeft: '40px solid transparent',
                    }}
                  />
                )}
                {/* Linea 1: numero (grande) + chip estado */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: 'var(--ink)',
                    letterSpacing: '0.02em',
                  }}>
                    #{b.numero}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                    {/* Chip tanda: la boleta comparte fórmula con otras (preparar juntas).
                        Si la mezcla ya se preparó, se marca en verde. */}
                    {b.estado === 'pendiente' && boletasEnTanda.has(Number(b.id)) && (() => {
                      const mezclaLista = !!(b as any).mezcla_preparada_at;
                      return (
                      <div style={{
                        padding: '4px 10px', borderRadius: '999px',
                        border: `1px solid ${mezclaLista ? 'var(--tone-success-border)' : 'var(--emp-accent)'}`,
                        color: mezclaLista ? 'var(--tone-success-text)' : 'var(--emp-accent-txt)',
                        background: mezclaLista ? 'var(--tone-success-bg)' : 'var(--emp-accent-bg)',
                        fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em',
                        whiteSpace: 'nowrap',
                      }}>{mezclaLista ? '✓ Mezcla lista' : `🧪 Tanda · ${boletasEnTanda.get(Number(b.id))!.n}`}</div>
                      );
                    })()}
                    {/* Chip avance de booms: aparece cuando bodega cerró algunos pero no todos */}
                    {despachoParcial && (
                      <div style={{
                        padding: '4px 10px', borderRadius: '999px',
                        border: '1px solid #3b82f6', color: 'var(--tone-info-text)',
                        background: 'rgba(59,130,246,0.12)',
                        fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em',
                        whiteSpace: 'nowrap',
                      }}>{cerradosB}/{totalBoomsB} booms</div>
                    )}
                    <div style={{
                      padding: '4px 10px', borderRadius: '999px',
                      border: `1px solid ${chipBorder}`, color: chipColor,
                      background: cedulaLista ? 'rgba(59,130,246,0.10)' : 'transparent',
                      fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                      whiteSpace: 'nowrap',
                    }}>{chipLabel}</div>
                  </div>
                </div>

                {/* Linea 2: Lote + Semana (titulo grande) */}
                <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--ink)', marginBottom: '4px', lineHeight: 1.2 }}>
                  Lote {b.lote_num ?? '—'}
                  <span style={{ fontSize: '14px', fontWeight: 600, color: col.text, marginLeft: '8px' }}>
                    Sem. {b.semana}
                  </span>
                </div>

                {/* Linea 3: etapa / finca */}
                <div style={{ fontSize: '15px', fontWeight: 500, color: 'var(--ink)', marginBottom: '6px', lineHeight: 1.3 }}>
                  {b.etapa_nombre ?? '—'}{b.finca_nombre ? <> &nbsp;·&nbsp; {b.finca_nombre}</> : null}
                </div>

                {/* Linea 4: chips de metricas (bloques, ha, plantas) */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', fontSize: '13px', color: 'var(--ink-muted)' }}>
                  {b.bloques && (
                    <span style={{ padding: '3px 10px', borderRadius: '6px', background: 'var(--surface-overlay)', border: '1px solid var(--line)', fontWeight: 500 }}>
                      Bloques {b.bloques}
                    </span>
                  )}
                  <span style={{ padding: '3px 10px', borderRadius: '6px', background: 'var(--surface-overlay)', border: '1px solid var(--line)', fontWeight: 500 }}>
                    {fmtN(b.area_ha, 2)} ha
                  </span>
                  <span style={{ padding: '3px 10px', borderRadius: '6px', background: 'var(--surface-overlay)', border: '1px solid var(--line)', fontWeight: 500 }}>
                    {fmtN(b.plantas, 0)} plantas
                  </span>
                </div>

                {b.estado === 'en_proceso' && b.despachado_por && (
                  <div style={{ fontSize: '13px', color: col.text, marginTop: '10px', fontStyle: 'italic', fontWeight: 500 }}>
                    Despachado por {b.despachado_por} · {fmtFecha(b.despachado_at?.slice(0,10))}
                  </div>
                )}

                {/* Info del reporte de campo (si existe) */}
                {(() => {
                  const rep = reportePorBoleta.get(Number(b.id));
                  if (!rep) return null;
                  const fechaIso = rep.fin_at || rep.registrado_at || rep.updated_at;
                  const fechaTxt = fechaIso
                    ? new Date(fechaIso).toLocaleString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : null;
                  const nombre = rep.boomero_nombre || rep.registrado_por;
                  // Coordenadas para Maps
                  const inicioCoord = (rep.inicio_lat != null && rep.inicio_lng != null)
                    ? `${Number(rep.inicio_lat).toFixed(6)}, ${Number(rep.inicio_lng).toFixed(6)}` : null;
                  const finCoord = (rep.fin_lat != null && rep.fin_lng != null)
                    ? `${Number(rep.fin_lat).toFixed(6)}, ${Number(rep.fin_lng).toFixed(6)}` : null;
                  // Duración
                  let duracion: string | null = null;
                  if (rep.inicio_at && rep.fin_at) {
                    const ms = new Date(rep.fin_at).getTime() - new Date(rep.inicio_at).getTime();
                    if (Number.isFinite(ms) && ms > 0) {
                      const m = Math.round(ms / 60000);
                      const h = Math.floor(m / 60);
                      const mm = m % 60;
                      duracion = h > 0 ? `${h}h ${mm}m` : `${mm} min`;
                    }
                  }
                  const fmtHora = (iso?: string | null) => iso
                    ? new Date(iso).toLocaleString('es-CR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                    : '—';
                  return (
                    <div style={{
                      marginTop: '12px', padding: '12px 14px', borderRadius: '10px',
                      background: 'rgba(59,130,246,0.10)',
                      border: '1px solid rgba(59,130,246,0.25)',
                      fontSize: '14px', color: 'var(--ink)', lineHeight: 1.5,
                    }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginBottom: '8px' }}>
                        {nombre && (
                          <span><span style={{ color: 'var(--tone-info-text)', fontWeight: 700 }}>Boomero:</span> <span style={{ fontWeight: 600 }}>{nombre}</span></span>
                        )}
                        {fechaTxt && (
                          <span><span style={{ color: 'var(--tone-info-text)', fontWeight: 700 }}>Aplicado:</span> <span style={{ fontWeight: 600 }}>{fechaTxt}</span></span>
                        )}
                        {duracion && (
                          <span><span style={{ color: 'var(--tone-info-text)', fontWeight: 700 }}>Duración:</span> <span style={{ fontWeight: 600 }}>{duracion}</span></span>
                        )}
                      </div>
                      {(inicioCoord || finCoord) && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '6px' }}>
                          {/* Inicio */}
                          <div style={{ padding: '8px 10px', borderRadius: '8px', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.35)' }}>
                            <div style={{ fontSize: '11px', color: '#4ade80', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>▶ Inicio</div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink)' }}>{fmtHora(rep.inicio_at)}</div>
                            {inicioCoord && (
                              <a
                                href={`https://www.google.com/maps?q=${rep.inicio_lat},${rep.inicio_lng}`}
                                target="_blank" rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{ fontSize: '11px', fontFamily: 'monospace', color: 'var(--tone-info-text)', textDecoration: 'underline', display: 'block', marginTop: '2px' }}
                              >
                                {inicioCoord}
                              </a>
                            )}
                          </div>
                          {/* Fin */}
                          <div style={{ padding: '8px 10px', borderRadius: '8px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)' }}>
                            <div style={{ fontSize: '11px', color: '#fca5a5', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>■ Fin</div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink)' }}>{fmtHora(rep.fin_at)}</div>
                            {finCoord && (
                              <a
                                href={`https://www.google.com/maps?q=${rep.fin_lat},${rep.fin_lng}`}
                                target="_blank" rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{ fontSize: '11px', fontFamily: 'monospace', color: '#fca5a5', textDecoration: 'underline', display: 'block', marginTop: '2px' }}
                              >
                                {finCoord}
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                      {rep.observaciones && rep.observaciones.trim() && (
                        <div style={{ marginTop: '8px', fontStyle: 'italic', color: 'var(--ink-muted)', fontSize: '13px' }}>
                          “{rep.observaciones}”
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Tab "En Proceso": pills por boom en modo LECTURA — el bodeguero
                    ve si cada boom YA FUE APLICADO en campo (✓ verde) o está
                    aplicándose (🚜 ámbar). NO se muestra el estado celeste "listo"
                    (info de bodega) para no confundir: solo importa el avance real
                    de campo. Sin acceso al Terminal Boomero, sin acción. */}
                {soloInfo && (() => {
                  const prog = boomsProgPorBoleta.get(Number(b.id)) ?? new Map<number, BoomProg>();
                  const nCompletos = Number(b.n_booms_completos || 0);
                  const tieneFrac = Number(b.litros_fraccion || 0) > 0;
                  const items: { num: number; label: string }[] = [];
                  for (let i = 1; i <= nCompletos; i++) items.push({ num: i, label: `Boom ${i}` });
                  if (tieneFrac) items.push({ num: 0, label: 'Fracción' });
                  // Estilo del chip — solo refleja estado de CAMPO (no el celeste de bodega):
                  //   ✓ verde  = aplicado en campo (fin)
                  //   🚜 ámbar = aplicándose (inicio sin fin)
                  //   ✕ rojo   = fracción descartada
                  //   ○ gris   = aún no aplicado en campo (sin distinguir si bodega lo cerró)
                  const chipDe = (bp: BoomProg | undefined) => {
                    if (bp?.descartado) return { icon: '✕', color: '#fca5a5', border: 'rgba(239,68,68,0.45)', bg: 'rgba(239,68,68,0.10)' };
                    if (bp?.aplicadoFin) return { icon: '✓', color: '#4ade80', border: 'rgba(34,197,94,0.45)', bg: 'rgba(34,197,94,0.16)' };
                    if (bp?.aplicadoInicio) return { icon: '🚜', color: '#fbbf24', border: 'rgba(251,191,36,0.5)', bg: 'rgba(251,191,36,0.14)' };
                    return { icon: '○', color: 'var(--ink-faint)', border: 'rgba(148,163,184,0.25)', bg: 'rgba(255,255,255,0.04)' };
                  };
                  return (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ fontSize: '10px', color: 'var(--ink-faint)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: '6px' }}>
                        Avance en campo (solo lectura)
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {items.map(it => {
                          const s = chipDe(prog.get(it.num));
                          return (
                            <span key={it.num} style={{
                              padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 700,
                              background: s.bg, border: `1px solid ${s.border}`, color: s.color,
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                            }}>
                              {s.icon} {it.label}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* CTA pegado al pie (no aplica en modo solo-info) */}
                {!soloInfo && (() => {
                  const repBoleta = reportePorBoleta.get(Number(b.id));
                  const tieneReporte = !!repBoleta;
                  const finalizado = !!repBoleta?.fin_at; // la aplicación realmente terminó
                  let label = '';
                  let bg = 'var(--surface-overlay)';
                  let color = col.text;
                  if (b.estado === 'pendiente') {
                    label = '→ Tocar para preparar despacho';
                  } else if (b.estado === 'en_proceso') {
                    if (finalizado) {
                      label = '✓ Tocar para CERRAR CÉDULA';
                      bg = 'rgba(34,197,94,0.12)';
                      color = '#4ade80';
                    } else if (tieneReporte) {
                      label = '⏳ Boomero aplicando — esperar a que finalice';
                      bg = 'rgba(245,158,11,0.10)';
                      color = '#fbbf24';
                    } else {
                      label = '⏳ Esperando reporte de campo del boomero';
                      bg = 'rgba(245,158,11,0.10)';
                      color = '#fbbf24';
                    }
                  } else if (b.estado === 'aplicada') {
                    if (finalizado) {
                      label = '→ Tocar para dar V°B°';
                    } else if (tieneReporte) {
                      label = '⚠ Boomero no marcó "Finalizar aplicación"';
                      bg = 'rgba(245,158,11,0.10)';
                      color = '#fbbf24';
                    } else {
                      label = '⚠ Falta reporte de campo del boomero';
                      bg = 'rgba(245,158,11,0.10)';
                      color = '#fbbf24';
                    }
                  }
                  return (
                    <div style={{
                      marginTop: '10px', padding: '10px', borderRadius: '8px',
                      background: bg,
                      fontSize: '13px', fontWeight: 700, color, textAlign: 'center',
                    }}>
                      {label}
                    </div>
                  );
                })()}
              </button>
            );
          })}
        </div>
        </div>
      </div>
    );
  }

  // ── Render vista de Tandas de mezcla (tabla dinámica: columna por lote) ────
  if (vista === 'tandas') {
    return (
      <div style={{ minHeight: '100%', background: 'var(--surface-base)' }}>
        <ConexionBanner />
        {renderMsgBanner()}
        <div style={{ padding: '16px', boxSizing: 'border-box', maxWidth: '100%' }}>
          {/* Cabecera global: descripción + selector de equipo (aplica a TODAS las tandas) */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', color: 'var(--ink-faint)', maxWidth: '420px' }}>
              {filtroDia ? `Pendientes del ${fmtFecha(filtroDia)}` : 'Todas las pendientes'} con fórmula idéntica (mismo plan, mismos productos y dosis)
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'var(--ink-muted)', fontWeight: 700, flexShrink: 0 }}>
              Equipo:
              <select
                className={selectCls}
                style={{ width: 'auto', minWidth: '200px', cursor: 'pointer' }}
                value={capacidadEquipo}
                onChange={e => setCapacidadEquipo(Number(e.target.value))}
              >
                {equipos.map(eq => <option key={eq.id} value={eq.capacidad_l}>{eq.nombre} ({fmtN(eq.capacidad_l, 0)} L)</option>)}
              </select>
            </label>
          </div>

          {/* Contenedor con altura mínima para que el "Analizando…" no cause salto */}
          <div style={{ minHeight: '200px' }}>
          {tandasLoading && <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-muted)' }}>Analizando fórmulas…</div>}

          {!tandasLoading && tandasData && tandasData.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--ink-muted)', fontSize: '16px' }}>
              No hay boletas con fórmula repetida {filtroDia ? `el ${fmtFecha(filtroDia)}` : ''}.<br />
              <span style={{ fontSize: '14px', color: 'var(--ink-faint)' }}>Cada fórmula se prepara por separado.</span>
            </div>
          )}

          {!tandasLoading && tandasData && tandasData.map((tanda) => {
            const lotes = tanda.boletas;
            // Recálculo de booms sobre el AGUA TOTAL de la tanda según el equipo
            // global elegido. Misma fórmula VFP (calcularBoleta) con area_ha=1
            // porque aguaTotal ya es el volumen total en litros.
            const cap = capacidadEquipo;
            const { n_booms_completos: nBooms, litros_fraccion: fraccion } = calcularBoleta(1, tanda.aguaTotal, cap);
            const nCheck = tanda.productos.filter(p => tandaCheck.has(`${tanda.firma}#${p.articulo}`)).length;
            const completa = nCheck === tanda.productos.length && tanda.productos.length > 0;
            // ¿Ya está marcada como preparada en BD? (todas las boletas con mezcla_preparada_at)
            const yaPreparada = tanda.boletas.every(tb => {
              const real = boletas.find(b => Number(b.id) === tb.id);
              return !!(real as any)?.mezcla_preparada_at;
            });
            return (
              <div key={tanda.firma} style={{ marginBottom: '24px', borderRadius: '14px', border: '1px solid var(--line)', overflow: 'hidden', background: 'var(--surface-raised)' }}>
                {/* Cabecera: ciclo + N lotes + agua + booms (sin selector, es global) */}
                <div style={{ padding: '14px 16px', background: 'var(--emp-accent-bg)', borderBottom: '1px solid var(--line)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--emp-accent-txt)' }}>{tanda.cicloNombre || 'Fórmula'}</div>
                      <div style={{ fontSize: '12px', color: 'var(--ink-muted)', marginTop: '2px' }}>{lotes.length} boletas · una sola mezcla</div>
                    </div>
                    <span style={{
                      fontSize: '12px', fontWeight: 800, padding: '4px 10px', borderRadius: '999px',
                      border: `1px solid ${completa ? '#22c55e' : 'var(--line)'}`,
                      background: completa ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.05)',
                      color: completa ? '#4ade80' : 'var(--ink-muted)',
                    }}>{completa ? '✓ ' : ''}{nCheck}/{tanda.productos.length} echados</span>
                  </div>
                  {/* Segunda fila: booms + agua total */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ padding: '6px 12px', borderRadius: '8px', background: 'var(--surface-overlay)', border: '1px solid var(--line)', fontSize: '13px', fontWeight: 700, color: 'var(--ink)' }}>
                      🛢️ {nBooms} booms de {fmtN(cap, 0)} L
                    </span>
                    {fraccion > 0 && (
                      <span style={{ padding: '6px 12px', borderRadius: '8px', background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.4)', fontSize: '13px', fontWeight: 700, color: '#fbbf24' }}>
                        + fracción {fmtN(fraccion, 0)} L
                      </span>
                    )}
                    <span style={{ fontSize: '13px', color: 'var(--tone-info-text)', fontWeight: 700, marginLeft: '2px' }}>
                      💧 {fmtN(tanda.aguaTotal, 0)} {tanda.aguaMedida} agua total
                    </span>
                  </div>
                </div>

                {/* Tabla dinámica: ✓ | Producto | Dosis | Lote A (#boleta) | ... | Total */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: `${460 + lotes.length * 100}px` }}>
                    <thead>
                      <tr style={{ color: 'var(--ink-faint)' }}>
                        <th style={{ width: '44px', padding: '10px 4px', position: 'sticky', left: 0, background: 'var(--surface-raised)' }} />
                        <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', position: 'sticky', left: '44px', background: 'var(--surface-raised)' }}>Producto</th>
                        <th style={{ textAlign: 'right', padding: '10px 8px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>Dosis</th>
                        <th style={{ textAlign: 'right', padding: '10px 8px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Agua fórm.</th>
                        {lotes.map(l => (
                          <th key={l.id} style={{ textAlign: 'right', padding: '10px 8px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                            <div>Lote {l.lote_num}</div>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--emp-accent-txt)', textTransform: 'none' }}>#{numBoletaDisplay(l.numero)}</div>
                          </th>
                        ))}
                        <th style={{ textAlign: 'right', padding: '10px 14px', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--emp-accent-txt)', whiteSpace: 'nowrap' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tanda.productos.map(p => {
                        const ckey = `${tanda.firma}#${p.articulo}`;
                        const listo = tandaCheck.has(ckey);
                        return (
                        <tr key={p.articulo} style={{ borderTop: '1px solid var(--line)', background: listo ? 'rgba(22,163,74,0.08)' : 'transparent' }}>
                          {/* Check: marca que el producto ya fue echado al tanque */}
                          <td style={{ padding: '6px 4px', textAlign: 'center', position: 'sticky', left: 0, background: listo ? 'var(--tone-success-bg)' : 'var(--surface-raised)' }}>
                            <button
                              onClick={() => setTandaCheck(prev => {
                                const s = new Set(prev);
                                s.has(ckey) ? s.delete(ckey) : s.add(ckey);
                                return s;
                              })}
                              title={listo ? 'Echado al tanque' : 'Marcar como echado'}
                              style={{
                                width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                                border: `2px solid ${listo ? '#22c55e' : 'rgba(148,163,184,0.3)'}`,
                                background: listo ? 'linear-gradient(135deg,#16a34a,#22c55e)' : 'rgba(255,255,255,0.04)',
                                color: '#fff', fontSize: '16px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background 0.2s, border-color 0.2s',
                              }}
                            >{listo ? '✓' : ''}</button>
                          </td>
                          <td style={{ padding: '8px 14px', color: 'var(--ink)', position: 'sticky', left: '44px', background: listo ? 'var(--tone-success-bg)' : 'var(--surface-raised)' }}>
                            <span style={{ fontFamily: 'monospace', color: 'var(--accent)', marginRight: '8px', fontWeight: 700 }}>{p.articulo}</span>
                            {p.nombre}
                          </td>
                          <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--ink-muted)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{fmtN(p.dosis, 2)}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--ink-faint)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{fmtN(tanda.aguaFormula, 0)}</td>
                          {lotes.map(l => (
                            <td key={l.id} style={{ padding: '8px 8px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--ink-muted)' }}>{fmtN(p.porBoleta[l.id] || 0, 2)}</td>
                          ))}
                          <td style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: 'var(--ink)', whiteSpace: 'nowrap' }}>
                            {fmtN(p.total, 2)} <span style={{ color: 'var(--ink-faint)', fontWeight: 500, fontSize: '11px' }}>{p.medida}</span>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Acción: marcar la mezcla como preparada (cuando el checklist está completo) */}
                <div style={{ padding: '12px 16px', borderTop: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  {yaPreparada ? (
                    <>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--tone-success-text)' }}>
                        ✓ Mezcla preparada
                      </span>
                      <button
                        onClick={() => marcarMezclaTanda(tanda, false)}
                        disabled={guardando}
                        style={{
                          padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 700,
                          border: '1px solid var(--line)', background: 'var(--surface-overlay)',
                          color: 'var(--ink-muted)', cursor: guardando ? 'wait' : 'pointer',
                        }}
                      >Desmarcar</button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '12px', color: 'var(--ink-faint)' }}>
                        {completa ? 'Todos los productos echados — confirme la mezcla.' : `Marque los ${tanda.productos.length - nCheck} producto(s) restantes.`}
                      </span>
                      <button
                        onClick={() => marcarMezclaTanda(tanda, true)}
                        disabled={!completa || guardando}
                        style={{
                          padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: 700,
                          border: 'none',
                          background: completa ? 'linear-gradient(135deg,#16a34a,#22c55e)' : 'var(--surface-overlay)',
                          color: completa ? '#fff' : 'var(--ink-faint)',
                          cursor: completa && !guardando ? 'pointer' : 'not-allowed',
                          opacity: guardando ? 0.6 : 1,
                        }}
                      >✓ Mezcla preparada</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </div>
    );
  }

  // ── Render despacho / confirmación ────────────────────────────────────────

  if ((vista === 'despacho' || vista === 'confirmacion') && boletaSel) {
    const esConfirmacion = vista === 'confirmacion';
    // Si el boom activo ya está cerrado por bodega → vista de solo lectura para
    // ese boom. El bodeguero puede consultarlo (clickeando el pill) pero no
    // editar productos ni cerrarlo de nuevo. soloLecturaBoom se trata casi
    // como esConfirmacion para inputs/botones de productos.
    const boomActivoItemRoot = boomsProgreso.find(b => b.boom_num === boomActivo);
    const soloLecturaBoom = !esConfirmacion && !!boomActivoItemRoot?.despachado;
    // accion: solo aplica en confirmacion (vob). Despacho usa cerrarBoomActivo/descartarFraccion.
    const accion = confirmarAplicacion;
    const colorAccion = esConfirmacion ? '#22c55e' : '#3b82f6';
    const totalProds = productos.filter(p => !p.es_agua && !p.excluida).length;
    const todosCheckeados = checkeados.size === totalProds && totalProds > 0;

    return (
      <div style={{ minHeight: '100%', background: 'var(--surface-base)' }}>
        <ConexionBanner />
        <div className="bod-despacho-wrap" style={{ padding: '16px', boxSizing: 'border-box', maxWidth: '100%' }}>

        {/* Header de la boleta */}
        <div style={{
          position: 'relative', overflow: 'hidden',
          padding: '16px 18px', borderRadius: '14px', marginBottom: '20px',
          background: 'var(--surface-raised)', border: '1px solid var(--line)',
        }}>
          {/* Esquina amarilla: boleta revisada/corregida manualmente */}
          {(boletaSel as any).revisada_manual && (
            <div
              title={(boletaSel as any).revisada_manual_nota || 'Boleta revisada/corregida manualmente'}
              style={{
                position: 'absolute', top: 0, right: 0, width: 0, height: 0,
                borderTop: '44px solid #facc15', borderLeft: '44px solid transparent',
              }}
            />
          )}
          {/* Línea 1: número de boleta + acción de reversión. (El "Volver" y el
              título de la vista viven en el navbar — no se repiten aquí.) */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
            <span style={{
              fontSize: '22px',
              fontWeight: 600,
              color: 'var(--ink)',
              fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
              letterSpacing: '0.02em',
            }}>
              #{boletaSel.numero}
            </span>
            {/* Solicitar reversión — el bodeguero envía la solicitud al
                ingeniero por email/WhatsApp; si la aprueba, el backend
                ejecuta la reversión y borra el trabajo del boomero. */}
            {esConfirmacion && (
              <button
                onClick={abrirSolicReversion}
                disabled={guardando}
                title="Solicitar al ingeniero revertir a pendiente"
                style={{
                  padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 700,
                  border: '1px solid rgba(245,158,11,0.5)',
                  background: 'rgba(245,158,11,0.10)',
                  color: '#fbbf24',
                  cursor: guardando ? 'wait' : 'pointer',
                  opacity: guardando ? 0.6 : 1,
                }}>
                ↺ Solicitar reversión
              </button>
            )}
          </div>
          <div style={{ fontSize: '17px', fontWeight: 800, color: 'var(--ink)' }}>
            Lote {boletaSel.lote_num} &nbsp;·&nbsp; {boletaSel.etapa_nombre} &nbsp;·&nbsp; Sem. {boletaSel.semana}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--ink-faint)', marginTop: '4px' }}>
            {boletaSel.bloques} &nbsp;·&nbsp; {fmtN(boletaSel.area_ha, 2)} ha &nbsp;·&nbsp; {fmtN(boletaSel.plantas, 0)} plantas
          </div>
        </div>

        {/* Wizard de booms — solo en vista despacho (no confirmacion) */}
        {!esConfirmacion && boomsProgreso.length > 0 && (() => {
          // Estado combinado bodega + boomero
          const totalAplicados = boomsProgreso.filter(b =>
            b.aplicado_fin_at != null || b.estado === 'descartado'
          ).length;
          const total = boomsProgreso.length;
          const boomActivoItem = boomsProgreso.find(b => b.boom_num === boomActivo);
          return (
            <div style={{
              padding: '14px 16px', borderRadius: '14px', marginBottom: '16px',
              background: 'var(--surface-raised)', border: '1px solid var(--line)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Carga del boom
                </div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: totalAplicados === total ? '#4ade80' : 'var(--ink-muted)' }}>
                  {totalAplicados} / {total} aplicadas
                </div>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {boomsProgreso.map((b) => {
                  const isActive = b.boom_num === boomActivo;
                  const bodCerrado = b.despachado && b.estado === 'despachado';
                  const bodDescartado = b.despachado && b.estado === 'descartado';
                  const boomeroIniciado = b.aplicado_inicio_at != null;
                  const boomeroFinalizado = b.aplicado_fin_at != null;
                  // Estados visuales por boom:
                  //   - "completo": bodega cerró + boomero finalizó
                  //   - "en_campo": bodega cerró, boomero iniciado o pendiente de regresar
                  //   - "preparando": bodega activo (preparando)
                  //   - "descartado": fracción descartada
                  //   - "pendiente": ni bodega ni boomero tocaron
                  let visualState: 'completo' | 'en_campo' | 'preparando' | 'descartado' | 'pendiente';
                  if (bodDescartado) visualState = 'descartado';
                  else if (bodCerrado && boomeroFinalizado) visualState = 'completo';
                  else if (bodCerrado) visualState = 'en_campo';
                  else if (isActive) visualState = 'preparando';
                  else visualState = 'pendiente';

                  // Navegación: el bodeguero prepara booms SIN esperar al boomero.
                  //  - Booms ya cerrados por bodega → navegables (modo consulta).
                  //  - El siguiente a preparar (primer no-cerrado) → siempre navegable.
                  // El orden secuencial de aplicación se controla en el Terminal Boomero,
                  // no en bodega.
                  const primerNoCerradoIdx = boomsProgreso.findIndex(x => !x.despachado);
                  const navegable = b.despachado || boomsProgreso.indexOf(b) === primerNoCerradoIdx;

                  const label = b.tipo === 'fraccion' ? 'Fracción' : `Boom ${b.boom_num}`;
                  // Iconos por estado
                  let icon: string, color: string, bg: string;
                  if (visualState === 'descartado') { icon = '✕'; color = '#ef4444'; bg = 'rgba(239,68,68,0.10)'; }
                  else if (visualState === 'completo') { icon = '✓✓'; color = '#22c55e'; bg = 'rgba(34,197,94,0.14)'; }
                  else if (visualState === 'en_campo') { icon = boomeroIniciado ? '🚜' : '✓·'; color = '#fbbf24'; bg = 'rgba(251,191,36,0.14)'; }
                  else if (visualState === 'preparando') { icon = '●'; color = '#3b82f6'; bg = 'rgba(59,130,246,0.14)'; }
                  else { icon = '○'; color = 'var(--ink-faint)'; bg = 'var(--surface-overlay)'; }

                  const titleParts: string[] = [];
                  if (bodCerrado) {
                    titleParts.push(`Bodega: cerrado por ${b.despachado_por || '—'}`);
                  } else if (bodDescartado) {
                    titleParts.push(`Bodega: descartada — ${b.motivo_descarte || ''}`);
                  } else {
                    titleParts.push('Bodega: pendiente');
                  }
                  if (boomeroFinalizado) {
                    titleParts.push(`Boomero: finalizado por ${b.aplicado_por || '—'}`);
                  } else if (boomeroIniciado) {
                    titleParts.push(`Boomero: en campo`);
                  } else if (bodCerrado) {
                    titleParts.push('Boomero: esperando arranque');
                  }

                  return (
                    <button
                      key={b.boom_num}
                      onClick={() => { if (navegable) { setBoomActivo(b.boom_num); setCheckeados(new Set()); } }}
                      disabled={!navegable}
                      title={titleParts.join(' | ')}
                      style={{
                        padding: '8px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 700,
                        border: `${isActive ? '2px' : '1.5px'} solid ${color}`, background: bg, color,
                        cursor: navegable ? 'pointer' : 'not-allowed',
                        opacity: navegable ? 1 : 0.55,
                        display: 'flex', alignItems: 'center', gap: '6px',
                        transition: 'all 0.2s',
                        boxShadow: isActive ? `0 0 0 3px ${bg}` : 'none',
                      }}
                    >
                      <span style={{ fontSize: '14px' }}>{icon}</span>
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Detalle del boom activo */}
              {boomActivoItem && (() => {
                const bodCerrado = boomActivoItem.despachado && boomActivoItem.estado === 'despachado';
                const bodDescartado = boomActivoItem.despachado && boomActivoItem.estado === 'descartado';
                const boomeroIniciado = boomActivoItem.aplicado_inicio_at != null;
                const boomeroFinalizado = boomActivoItem.aplicado_fin_at != null;
                return (
                  <div style={{
                    marginTop: '12px', padding: '10px 12px', borderRadius: '10px',
                    background: 'var(--surface-overlay)', border: '1px solid var(--line-dim)',
                    fontSize: '12.5px', lineHeight: 1.5,
                  }}>
                    {/* Línea 1: Bodega */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                      <span style={{ color: 'var(--ink-muted)', fontWeight: 700 }}>Bodega</span>
                      <span style={{ color: bodDescartado ? '#fca5a5' : bodCerrado ? '#4ade80' : 'var(--ink-faint)' }}>
                        {bodDescartado ? '✕ Descartada' :
                         bodCerrado ? `✓ Cerrada por ${boomActivoItem.despachado_por || '—'}` :
                         '⌛ Pendiente de preparar'}
                      </span>
                    </div>
                    {/* Línea 2: Boomero */}
                    {!bodDescartado && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginTop: '4px' }}>
                        <span style={{ color: 'var(--ink-muted)', fontWeight: 700 }}>Boomero</span>
                        <span style={{ color: boomeroFinalizado ? '#4ade80' : boomeroIniciado ? '#fbbf24' : 'var(--ink-faint)' }}>
                          {boomeroFinalizado ? `✓ Finalizada por ${boomActivoItem.aplicado_por || '—'}` :
                           boomeroIniciado ? `🚜 Aplicando en campo${boomActivoItem.aplicado_por ? ` (${boomActivoItem.aplicado_por})` : ''}` :
                           bodCerrado ? '⌛ Esperando que arranque en campo' :
                           '— (esperando preparación)'}
                        </span>
                      </div>
                    )}
                    {/* Hint según estado */}
                    {!bodCerrado && !bodDescartado && (
                      <div style={{ marginTop: '6px', fontSize: '11.5px', color: 'var(--ink-faint)', fontStyle: 'italic' }}>
                        {boomActivoItem.tipo === 'fraccion'
                          ? <>Prepare la fracción ({fmtN(boletaSel.litros_fraccion ?? 0, 0)} L) y ciérrela cuando el boomero salga.</>
                          : <>Prepare el boom {boomActivoItem.boom_num} ({fmtN(boletaSel.boom_capacidad_l ?? 0, 0)} L) y ciérrelo cuando el boomero salga.</>}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {renderMsgBanner()}

        {/* Lista de productos */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
          {/* Agua requerida — primero, solo lectura, con BOOM / FRACCIÓN / TOTAL */}
          {productos.filter(p => p.es_agua).map(p => {
            const boomCap = Number(boletaSel?.boom_capacidad_l ?? 0);
            const lFraccion = Number(boletaSel?.litros_fraccion ?? 0);
            const boomActivoItem = boomsProgreso.find(b => b.boom_num === boomActivo);
            const fraccionActiva = !esConfirmacion && boomActivoItem?.tipo === 'fraccion';
            const boomCompletoActivo = !esConfirmacion && boomActivoItem?.tipo === 'completo';
            return (
              <div key={p.id} className="bod-agua-row" style={{
                padding: '10px 16px', borderRadius: '10px',
                border: '1.5px solid rgba(56,189,248,0.35)',
                background: 'linear-gradient(90deg, rgba(56,189,248,0.12) 0%, rgba(56,189,248,0.06) 100%)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
              }}>
                <span style={{ fontSize: '15px', color: 'var(--ink-muted)', fontWeight: 600 }}>
                  <span style={{ color: 'var(--ink-faint)', marginRight: '8px', fontFamily: 'monospace' }}>
                    {p.articulo}
                  </span>
                  AGUA REQUERIDA
                </span>
                <div className="bod-agua-chips" style={{ display: 'grid', gridTemplateColumns: '84px 84px 104px', gap: '6px', alignItems: 'center' }}>
                  <div style={{
                    padding: '5px 8px', borderRadius: '8px',
                    border: boomCompletoActivo ? '2px solid #22c55e' : '1px solid var(--line-dim)',
                    background: boomCompletoActivo ? 'rgba(34,197,94,0.16)' : 'rgba(34,197,94,0.06)',
                    textAlign: 'right',
                    opacity: fraccionActiva ? 0.45 : 1,
                    transition: 'all 0.2s',
                  }}>
                    <div style={{ fontSize: '9px', color: boomCompletoActivo ? '#4ade80' : 'var(--ink-muted)', marginBottom: '1px', fontWeight: 700, letterSpacing: '0.04em' }}>BOOM{boomCompletoActivo ? ' ●' : ''}</div>
                    <div style={{ fontSize: '14px', lineHeight: 1.05, fontWeight: 800, color: '#4ade80', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtN(boomCap, 0)}
                    </div>
                  </div>
                  <div style={{
                    padding: '5px 8px', borderRadius: '8px',
                    border: fraccionActiva ? '2px solid #fbbf24' : '1px solid var(--line-dim)',
                    background: fraccionActiva ? 'rgba(251,191,36,0.18)' : 'rgba(251,191,36,0.06)',
                    textAlign: 'right',
                    opacity: boomCompletoActivo ? 0.45 : 1,
                    transition: 'all 0.2s',
                  }}>
                    <div style={{ fontSize: '9px', color: fraccionActiva ? '#fbbf24' : 'var(--ink-muted)', marginBottom: '1px', fontWeight: 700, letterSpacing: '0.04em' }}>FRACCIÓN{fraccionActiva ? ' ●' : ''}</div>
                    <div style={{ fontSize: '14px', lineHeight: 1.05, fontWeight: 800, color: '#fbbf24', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtN(lFraccion, 0)}
                    </div>
                  </div>
                  <div style={{
                    padding: '5px 8px', borderRadius: '8px',
                    border: '1px solid var(--line)', background: 'rgba(147,197,253,0.06)', textAlign: 'right',
                  }}>
                    <div style={{ fontSize: '9px', color: 'var(--ink-muted)', marginBottom: '1px', fontWeight: 700 }}>TOTAL</div>
                    <div style={{ fontSize: '16px', lineHeight: 1.05, fontWeight: 800, color: 'var(--tone-info-text)', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtN(p.cantidad_aplicar, 0)} <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--ink-faint)' }}>Lts</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {productos.filter(p => !p.es_agua).map((p, idx) => {
            const listo = checkeados.has(idx);
            const ajusteAprobado = ['aprobada', 'modificada'].includes(String(p.solicitud_estado || ''));
            const ajusteRechazado = p.solicitud_estado === 'rechazada';
            const resaltarAprobacion = productosFlash.has(Number(p.id));
            const resaltarRechazo = productosRechazadosFlash.has(Number(p.id));
            return (
            <div
              key={p.id}
              className={`bod-prod-row${resaltarAprobacion ? ' bod-row-approved-flash' : resaltarRechazo ? ' bod-row-rejected-flash' : ''}`}
              style={{
              padding: '8px 10px', borderRadius: '10px',
              border: `1.5px solid ${p.excluida ? 'rgba(148,163,184,0.25)' : listo ? '#16a34a' : ajusteRechazado ? '#ef4444' : p.ajustado ? '#f59e0b' : 'var(--line)'}`,
              background: p.excluida ? 'rgba(100,116,139,0.10)' : listo ? 'rgba(22,163,74,0.08)' : ajusteRechazado ? 'rgba(239,68,68,0.07)' : p.ajustado ? 'rgba(245,158,11,0.07)' : 'var(--surface-raised)',
              opacity: p.excluida ? 0.6 : 1,
              transition: 'all 0.2s',
              display: 'grid',
              gridTemplateColumns: 'minmax(260px, 1fr) auto',
              alignItems: 'center',
              columnGap: '12px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px' }}>
                    {/* Botón check — solo aparece en Despacho de un boom NO cerrado (verificación de productos) */}
                    {!esConfirmacion && !soloLecturaBoom && p.excluida && (
                      <div
                        title="Producto excluido por el ingeniero"
                        style={{
                          width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                          border: '2px solid #ef4444',
                          background: 'linear-gradient(135deg,#991b1b,#ef4444)',
                          color: '#fff', fontSize: '17px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 800,
                        }}
                      >✕</div>
                    )}
                    {!esConfirmacion && !soloLecturaBoom && !p.excluida && (
                      <button
                        onClick={() => setCheckeados(prev => {
                          const s = new Set(prev);
                          s.has(idx) ? s.delete(idx) : s.add(idx);
                          return s;
                        })}
                        className={listo ? 'bod-check-done' : 'bod-check-pend'}
                        style={{
                          width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                          border: `2px solid ${listo ? '#22c55e' : 'rgba(148,163,184,0.3)'}`,
                          background: listo ? 'linear-gradient(135deg,#16a34a,#22c55e)' : 'rgba(255,255,255,0.04)',
                          color: '#fff', fontSize: '17px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'background 0.2s, border-color 0.2s',
                        }}
                      >{listo ? '✓' : ''}</button>
                    )}
                    <div style={{
                      fontSize: '15px', lineHeight: 1.15, fontWeight: 800,
                      color: p.excluida ? 'var(--ink-faint)' : (listo && !esConfirmacion) ? '#4ade80' : 'var(--ink)',
                      textDecoration: p.excluida ? 'line-through' : (listo && !esConfirmacion) ? 'line-through' : 'none',
                      opacity: p.excluida ? 0.7 : 1,
                    }}>
                      {p.nombre_producto}
                    </div>
                    {p.agregado_manualmente && (
                      <span style={{
                        fontSize: '10px', fontWeight: 800, padding: '2px 7px', borderRadius: '999px',
                        border: '1px solid rgba(167,139,250,0.45)',
                        background: 'rgba(124,58,237,0.15)',
                        color: '#c4b5fd', whiteSpace: 'nowrap', flexShrink: 0,
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                      }}>Inclusión manual</span>
                    )}
                    {puedeVerSaldos && !p.es_agua && !p.excluida && (() => {
                      const s = saldos.get(Number(p.id));
                      if (!s) return null;
                      if (s.sin_enlace) {
                        return (
                          <span title="Producto sin enlace al inventario ERP" style={{
                            fontSize: '10px', fontWeight: 800, padding: '2px 7px', borderRadius: '999px',
                            border: '1px solid rgba(148,163,184,0.4)',
                            background: 'rgba(148,163,184,0.12)',
                            color: 'var(--ink-faint)', whiteSpace: 'nowrap', flexShrink: 0,
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                          }}>Sin enlace inv.</span>
                        );
                      }
                      const ok = s.tiene_saldo;
                      const margen = s.disponible_neto - s.requerido;
                      const ajustado = ok && margen < (s.requerido * 0.2); // < 20% margen
                      const color = !ok ? '#ef4444' : ajustado ? '#f59e0b' : '#22c55e';
                      const bg = !ok ? 'rgba(239,68,68,0.12)' : ajustado ? 'rgba(245,158,11,0.12)' : 'rgba(34,197,94,0.12)';
                      const label = !ok ? `⛔ Saldo ${fmtN(s.disponible_neto, 1)} / ${fmtN(s.requerido, 1)}`
                        : ajustado ? `⚠ Saldo ajustado ${fmtN(s.disponible_neto, 1)}`
                        : `✓ Saldo ${fmtN(s.disponible_neto, 1)}`;
                      // Otras boletas pendientes (aún sin reservar) que piden el mismo
                      // producto: no reserva ni bloquea nada, solo avisa si esa demanda
                      // haría que el saldo ya no alcance cuando se despachen.
                      const demandaOtras = s.demanda_pendiente_otras || 0;
                      const riesgoPendientes = ok && demandaOtras > 0 && !s.tiene_saldo_considerando_pendientes;
                      return (
                        <>
                          <span title={`Stock: ${fmtN(s.stock_actual, 3)} | Comprometido: ${fmtN(s.cantidad_comprometida, 3)} | Disponible: ${fmtN(s.disponible_neto, 3)} | Requerido: ${fmtN(s.requerido, 3)}`}
                            style={{
                              fontSize: '10px', fontWeight: 800, padding: '2px 7px', borderRadius: '999px',
                              border: `1px solid ${color}`, background: bg, color,
                              whiteSpace: 'nowrap', flexShrink: 0,
                              textTransform: 'uppercase', letterSpacing: '0.04em',
                            }}>{label}</span>
                          {demandaOtras > 0 && (
                            <span title={`${fmtN(demandaOtras, 3)} más pedido por otra(s) boleta(s) pendiente(s) del mismo producto (aún sin despachar): ${(s.boletas_pendientes_otras || []).join(', ')}. No reserva nada — solo aviso.`}
                              style={{
                                fontSize: '10px', fontWeight: 800, padding: '2px 7px', borderRadius: '999px',
                                border: `1px solid ${riesgoPendientes ? '#f59e0b' : 'rgba(148,163,184,0.4)'}`,
                                background: riesgoPendientes ? 'rgba(245,158,11,0.12)' : 'rgba(148,163,184,0.12)',
                                color: riesgoPendientes ? '#f59e0b' : 'var(--ink-faint)',
                                whiteSpace: 'nowrap', flexShrink: 0,
                                textTransform: 'uppercase', letterSpacing: '0.04em',
                              }}>⏳ +{fmtN(demandaOtras, 1)} pendiente</span>
                          )}
                        </>
                      );
                    })()}
                    {p.excluida && (
                      <span style={{
                        fontSize: '10px', fontWeight: 800, padding: '2px 7px', borderRadius: '999px',
                        border: '1px solid rgba(239,68,68,0.55)',
                        background: 'rgba(239,68,68,0.15)',
                        color: '#fca5a5', whiteSpace: 'nowrap', flexShrink: 0,
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                      }}>Excluida</span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--ink-muted)', marginTop: '1px' }}>
                    {p.articulo} &nbsp;·&nbsp; {p.medida}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--ink-faint)', marginTop: '1px' }}>
                    Dosis: {fmtN(p.dosis)} &nbsp;·&nbsp; Reingreso: {p.reingreso ?? 'N/A'}
                  </div>
                </div>
                {/* Cantidad planificada */}
                <div style={{ display: 'none' }}>
                  <div style={{ fontSize: '11px', color: 'var(--ink-muted)', marginBottom: '2px' }}>PLANIFICADO</div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--tone-info-text)', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtN(p.cantidad_aplicar)}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--ink-faint)' }}>{p.medida}</div>
                </div>
              </div>

              <div className="bod-prod-right" style={{
                marginTop: 0,
                display: 'grid',
                gridTemplateColumns: p.ajustado ? '78px 78px 96px 112px auto' : '78px 78px 96px 112px auto',
                alignItems: 'center',
                justifyContent: 'end',
                gap: '6px',
              }}>
                {(() => {
                  // En vista despacho: resaltar la columna correspondiente al boom activo.
                  const boomActivoItem = boomsProgreso.find(b => b.boom_num === boomActivo);
                  const fraccionActiva = !esConfirmacion && boomActivoItem?.tipo === 'fraccion';
                  const boomCompletoActivo = !esConfirmacion && boomActivoItem?.tipo === 'completo';
                  return (
                    <>
                      {/* BOOM COMPLETO — cantidad por cada boom de capacidad total */}
                      <div style={{
                        padding: '5px 8px',
                        borderRadius: '8px',
                        border: boomCompletoActivo
                          ? '2px solid #22c55e'
                          : `1px solid ${p.ajustado ? 'rgba(245,158,11,0.4)' : 'var(--line-dim)'}`,
                        background: boomCompletoActivo ? 'rgba(34,197,94,0.16)' : 'rgba(34,197,94,0.06)',
                        textAlign: 'right',
                        opacity: !esConfirmacion && fraccionActiva ? 0.45 : 1,
                        transition: 'all 0.2s',
                      }}>
                        <div style={{ fontSize: '9px', color: boomCompletoActivo ? '#4ade80' : 'var(--ink-muted)', marginBottom: '1px', fontWeight: 700, letterSpacing: '0.04em' }}>
                          BOOM{p.ajustado ? ' ⚙' : ''}{boomCompletoActivo ? ' ●' : ''}
                        </div>
                        <div style={{ fontSize: '14px', lineHeight: 1.05, fontWeight: 800, color: '#4ade80', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtN(p.cantidad_boom_efectiva ?? p.cantidad_boom ?? 0, 3)}
                        </div>
                      </div>
                      {/* FRACCIÓN — cantidad para el boom parcial */}
                      <div style={{
                        padding: '5px 8px',
                        borderRadius: '8px',
                        border: fraccionActiva
                          ? '2px solid #fbbf24'
                          : `1px solid ${p.ajustado ? 'rgba(245,158,11,0.4)' : 'var(--line-dim)'}`,
                        background: fraccionActiva ? 'rgba(251,191,36,0.18)' : 'rgba(251,191,36,0.06)',
                        textAlign: 'right',
                        opacity: !esConfirmacion && boomCompletoActivo ? 0.45 : 1,
                        transition: 'all 0.2s',
                      }}>
                        <div style={{ fontSize: '9px', color: fraccionActiva ? '#fbbf24' : 'var(--ink-muted)', marginBottom: '1px', fontWeight: 700, letterSpacing: '0.04em' }}>
                          FRACCIÓN{p.ajustado ? ' ⚙' : ''}{fraccionActiva ? ' ●' : ''}
                        </div>
                        <div style={{ fontSize: '14px', lineHeight: 1.05, fontWeight: 800, color: '#fbbf24', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtN(p.cantidad_fraccion_efectiva ?? p.cantidad_fraccion ?? 0, 3)}
                        </div>
                      </div>
                    </>
                  );
                })()}
                <div className="bod-prod-plan" style={{
                  padding: '5px 8px',
                  borderRadius: '8px',
                  border: '1px solid var(--line)',
                  background: 'rgba(147,197,253,0.06)',
                  textAlign: 'right',
                }}>
                  <div className="bod-prod-plan-label" style={{ fontSize: '9px', color: 'var(--ink-muted)', marginBottom: '1px', fontWeight: 700 }}>
                    TOTAL
                  </div>
                  <div className="bod-prod-plan-num" style={{ fontSize: '16px', lineHeight: 1.05, fontWeight: 800, color: 'var(--tone-info-text)', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtN(p.cantidad_aplicar, 3)}
                  </div>
                </div>
                <span className="bod-prod-input-label" style={{ display: 'none' }}>Cambio</span>
                <div className="bod-prod-input-wrap" style={{ minWidth: 0 }}>
                  <div style={{ display: 'none' }}>
                    CANTIDAD REAL DESPACHADA
                  </div>
                  <input
                    className="bod-prod-input"
                    type="text"
                    inputMode="decimal"
                    value={p.cantidad_raw}
                    disabled={ajusteAprobado || esConfirmacion || soloLecturaBoom || p.excluida}
                    readOnly={esConfirmacion || soloLecturaBoom || p.excluida}
                    onFocus={e => {
                      if (ajusteAprobado || esConfirmacion) return;
                      // al enfocar muestra el número plano sin formato de miles
                      const plain = String(p.cantidad_real);
                      setProductos(prev => prev.map((pp, i) =>
                        i === idx ? { ...pp, cantidad_raw: plain } : pp
                      ));
                      setTimeout(() => e.target.select(), 0);
                    }}
                    onChange={e => {
                      if (ajusteAprobado) return;
                      const raw = e.target.value.replace(/[^0-9.,]/g, '');
                      setProductos(prev => prev.map((pp, i) =>
                        i === idx ? { ...pp, cantidad_raw: raw } : pp
                      ));
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        (e.target as HTMLInputElement).blur();
                        // Saltar al botón Motivo de la misma fila si está visible
                        setTimeout(() => {
                          const btn = document.querySelector<HTMLButtonElement>(`button[data-motivo-idx="${idx}"]`);
                          btn?.focus();
                        }, 0);
                      }
                    }}
                    onBlur={e => {
                      if (ajusteAprobado) return;
                      // normaliza coma → punto para parsear, luego formatea "9,999.999"
                      const nueva = parseFloat(e.target.value.replace(/,(?=\d{3})/g, '').replace(',', '.')) || 0;
                      const formatted = nueva.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
                      const nBoomsCompletos = Number(boletaSel?.n_booms_completos ?? 0);
                      setProductos(prev => prev.map((pp, i) => {
                        if (i !== idx) return pp;
                        const ajustado = Math.abs(nueva - (pp.cantidad_aplicar ?? 0)) > 0.001;
                        const cambioCantidad = Math.abs(nueva - pp.cantidad_real) > 0.001;
                        // Redistribuir proporcional al volumen cuando el bodeguero cambia el total
                        const dist = ajustado
                          ? redistribuirBoomFraccion(
                              nueva,
                              Number(pp.cantidad_boom ?? 0),
                              Number(pp.cantidad_fraccion ?? 0),
                              nBoomsCompletos,
                            )
                          : { cantidad_boom: Number(pp.cantidad_boom ?? 0), cantidad_fraccion: Number(pp.cantidad_fraccion ?? 0) };
                    return { ...pp, cantidad_real: nueva, cantidad_raw: formatted, ajustado,
                      razon_codigo: ajustado || (!cambioCantidad && pp.solicitud_estado === 'rechazada') ? pp.razon_codigo : '',
                      razon_detalle: ajustado || (!cambioCantidad && pp.solicitud_estado === 'rechazada') ? pp.razon_detalle : '',
                      solicitud_id: !cambioCantidad ? pp.solicitud_id : undefined,
                      solicitud_estado: !cambioCantidad ? pp.solicitud_estado : undefined,
                      cantidad_aprobada: !cambioCantidad ? pp.cantidad_aprobada : undefined,
                      cantidad_boom_efectiva: dist.cantidad_boom,
                      cantidad_fraccion_efectiva: dist.cantidad_fraccion };
                  }));
                }}
                    style={{
                      minWidth: 0, width: '112px', padding: '6px 8px', borderRadius: '8px', fontSize: '16px',
                      fontWeight: 700, fontVariantNumeric: 'tabular-nums', textAlign: 'right',
                      border: `1.5px solid ${ajusteAprobado ? '#22c55e' : p.ajustado ? '#f59e0b' : 'var(--line)'}`,
                      background: ajusteAprobado ? 'rgba(34,197,94,0.08)' : esConfirmacion ? 'var(--surface-overlay)' : 'var(--surface-base)',
                      color: ajusteAprobado ? '#4ade80' : p.ajustado ? '#fbbf24' : 'var(--ink)',
                      outline: 'none',
                      cursor: (ajusteAprobado || esConfirmacion) ? 'not-allowed' : 'text',
                      opacity: esConfirmacion && !ajusteAprobado ? 0.85 : 1,
                    }}
                  />
                </div>
                {/* Botón 'Cambiar producto' — solicita al ingeniero reemplazar este producto */}
                {/* Requisito: el producto NO debe estar marcado como alistado (check verde) */}
                {!esConfirmacion && !soloLecturaBoom && !p.es_agua && !ajusteAprobado && !p.excluida && !listo && p.solicitud_estado !== 'pendiente' && (
                  <button
                    onClick={() => abrirCambioProd(Number(p.id))}
                    title="Solicitar cambio de producto al ingeniero"
                    style={{
                      minHeight: '34px', flexShrink: 0, padding: '7px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                      border: '1.5px solid rgba(139,92,246,0.5)',
                      background: 'rgba(139,92,246,0.10)',
                      color: '#a78bfa',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}>
                    🔄 Cambiar
                  </button>
                )}
                {/* Botón 'Excluir' — solicita al ingeniero quitar este producto de la boleta */}
                {/* Requisito: el producto NO debe estar marcado como alistado (check verde) */}
                {!esConfirmacion && !soloLecturaBoom && !p.es_agua && !p.excluida && !listo && p.solicitud_estado !== 'pendiente' && (
                  <button
                    onClick={() => abrirExcluirProd(Number(p.id))}
                    title="Solicitar excluir este producto"
                    style={{
                      minHeight: '34px', flexShrink: 0, padding: '7px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                      border: '1.5px solid rgba(239,68,68,0.5)',
                      background: 'rgba(239,68,68,0.10)',
                      color: '#fca5a5',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}>
                    ⛔ Excluir
                  </button>
                )}
                {(p.ajustado || (p.solicitud_estado && !ajusteRechazado)) && (
                  <button data-motivo-idx={idx} className="bod-prod-motivo" onClick={() => {
                    if (ajusteAprobado) {
                      solicitarReversionAjuste(idx);
                      return;
                    }
                    if (p.solicitud_estado === 'pendiente') {
                      showMsg('ok', 'La solicitud esta pendiente con el ingeniero.');
                      return;
                    }
                    p.razon_codigo ? solicitarAjuste(idx) : abrirModalAjuste(idx);
                  }} style={{
                    minHeight: '34px', flexShrink: 0, padding: '7px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 800,
                    border: `1.5px solid ${ajusteAprobado ? '#22c55e' : ajusteRechazado ? '#ef4444' : '#f59e0b'}`,
                    background: ajusteAprobado ? 'rgba(34,197,94,0.1)' : ajusteRechazado ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
                    color: ajusteAprobado ? '#4ade80' : ajusteRechazado ? '#fca5a5' : '#fbbf24',
                    cursor: 'pointer',
                  }}>
                    <span>{p.solicitud_estado === 'pendiente' ? 'Pendiente' : p.solicitud_estado === 'modificada' ? 'Modificada' : p.solicitud_estado === 'aprobada' ? 'Aprobada' : ajusteRechazado ? 'Rechazado' : p.razon_codigo ? 'Solicitar' : 'Motivo'}</span>
                    <span style={{ display: 'none' }}>
                    {p.razon_codigo ? '✓ Justificado' : '⚠ Justificar'}
                    </span>
                  </button>
                )}
                {/* Anular solicitud pendiente → la línea vuelve a su estado original */}
                {p.solicitud_estado === 'pendiente' && !esConfirmacion && !soloLecturaBoom && (
                  <button
                    title="Anular la solicitud y volver la línea a su estado original"
                    disabled={guardando}
                    onClick={() => anularSolicitud(idx)}
                    style={{
                      minHeight: '34px', flexShrink: 0, padding: '7px 10px', borderRadius: '8px',
                      fontSize: '12px', fontWeight: 800, marginLeft: '6px',
                      border: '1.5px solid #ef4444', background: 'rgba(239,68,68,0.1)',
                      color: '#fca5a5', cursor: guardando ? 'default' : 'pointer', opacity: guardando ? 0.6 : 1,
                    }}
                  >
                    ✕ Anular
                  </button>
                )}
              </div>
              {ajusteRechazado && (
                <div style={{
                  marginTop: '8px', fontSize: '12px', color: '#fca5a5',
                  padding: '6px 10px', borderRadius: '6px',
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.22)',
                }}>
                  Solicitud rechazada. Puede corregir la cantidad y volver a solicitar el cambio.
                </div>
              )}
              {p.razon_codigo && (
                <div style={{
                  marginTop: '8px', fontSize: '12px',
                  color: ajusteRechazado ? 'var(--ink-muted)' : '#4ade80',
                  padding: '6px 10px', borderRadius: '6px',
                  background: ajusteRechazado ? 'rgba(148,163,184,0.06)' : 'rgba(34,197,94,0.08)',
                }}>
                  <div>
                    {RAZONES_AJUSTE.find(r => r.codigo === p.razon_codigo)?.label}
                    {p.razon_detalle ? ` — ${p.razon_detalle}` : ''}
                  </div>
                  {p.respuesta_comentario && (
                    <div style={{
                      marginTop: '6px',
                      paddingTop: '6px',
                      borderTop: '1px dashed var(--line)',
                      color: 'var(--tone-info-text)',
                      fontStyle: 'italic',
                    }}>
                      <span style={{ fontWeight: 700, fontStyle: 'normal' }}>Ingeniero:</span> {p.respuesta_comentario}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}

        </div>

        {/* Botón Agregar producto nuevo — solo en boletas pendientes (despacho) */}
        {!esConfirmacion && String(boletaSel?.estado || '').toLowerCase() === 'pendiente' && (
          <button
            onClick={() => abrirAgregarProd()}
            style={{
              width: '100%', marginBottom: '12px', padding: '10px',
              borderRadius: '10px', fontSize: '13px', fontWeight: 700,
              border: '1.5px dashed rgba(139,92,246,0.5)',
              background: 'rgba(139,92,246,0.06)',
              color: '#a78bfa', cursor: 'pointer',
            }}>
            ➕ Solicitar agregar producto
          </button>
        )}

        {/* Indicador de progreso — solo cuando estamos verificando un boom NO cerrado */}
        {!esConfirmacion && !soloLecturaBoom && (() => {
          const boomActivoItem = boomsProgreso.find(b => b.boom_num === boomActivo);
          const yaCerrado = boomActivoItem?.despachado === true;
          const labelBoom = boomActivoItem?.tipo === 'fraccion' ? 'la fracción' : `boom ${boomActivo}`;
          return (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              marginBottom: '12px', padding: '10px 14px', borderRadius: '10px',
              background: todosCheckeados ? 'rgba(22,163,74,0.1)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${todosCheckeados ? 'rgba(34,197,94,0.3)' : 'var(--line)'}`,
              transition: 'all 0.3s',
              opacity: yaCerrado ? 0.5 : 1,
            }}>
              <div style={{ flex: 1, height: '6px', borderRadius: '999px', background: 'var(--line-dim)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '999px',
                  background: 'linear-gradient(90deg,#16a34a,#22c55e)',
                  width: `${totalProds > 0 ? (checkeados.size / totalProds) * 100 : 0}%`,
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <span style={{ fontSize: '13px', fontWeight: 700, color: todosCheckeados ? '#4ade80' : 'var(--ink-muted)', flexShrink: 0 }}>
                {checkeados.size}/{totalProds} verificados para {labelBoom}
              </span>
            </div>
          );
        })()}

        {/* Banner: bloqueo si falta reporte de campo o no se finalizó (solo confirmacion) */}
        {esConfirmacion && !reporteCampo?.id && (
          <div style={{
            marginBottom: '12px', padding: '12px 14px', borderRadius: '10px',
            background: 'rgba(245,158,11,0.10)', border: '1.5px solid rgba(245,158,11,0.4)',
            display: 'flex', alignItems: 'flex-start', gap: '10px',
          }}>
            <span style={{ fontSize: '20px' }}>⚠</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#fbbf24', marginBottom: '2px' }}>
                Falta el reporte de campo
              </div>
              <div style={{ fontSize: '12.5px', color: 'var(--ink-muted)', lineHeight: 1.4 }}>
                El boomero debe llenar el reporte de campo (Terminal Boomero) antes de que se pueda confirmar la aplicación.
              </div>
            </div>
          </div>
        )}
        {esConfirmacion && reporteCampo?.id && !reporteCampo?.fin_at && (
          <div style={{
            marginBottom: '12px', padding: '12px 14px', borderRadius: '10px',
            background: 'rgba(245,158,11,0.10)', border: '1.5px solid rgba(245,158,11,0.4)',
            display: 'flex', alignItems: 'flex-start', gap: '10px',
          }}>
            <span style={{ fontSize: '20px' }}>⏳</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#fbbf24', marginBottom: '2px' }}>
                Aplicación en curso — boomero no ha finalizado
              </div>
              <div style={{ fontSize: '12.5px', color: 'var(--ink-muted)', lineHeight: 1.4 }}>
                El boomero debe presionar "Finalizar aplicación" para poder confirmar.
              </div>
            </div>
          </div>
        )}
        {esConfirmacion && reporteCampo?.fin_at && (
          <div style={{
            marginBottom: '12px', padding: '8px 12px', borderRadius: '8px',
            background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)',
            fontSize: '12.5px', color: '#4ade80',
          }}>
            ✓ Aplicación finalizada por {reporteCampo.registrado_por || 'boomero'}
          </div>
        )}

        {/* Botón acción */}
        {esConfirmacion ? (
          <button
            onClick={accion}
            disabled={guardando || !reporteCampo?.fin_at}
            style={{
              width: '100%', padding: '18px', borderRadius: '14px', fontSize: '18px', fontWeight: 800,
              border: 'none', letterSpacing: '0.02em', transition: 'all 0.3s',
              background: guardando || !reporteCampo?.fin_at ? 'rgba(255,255,255,0.07)' : colorAccion,
              color: guardando || !reporteCampo?.fin_at ? 'rgba(255,255,255,0.3)' : '#fff',
              cursor: guardando || !reporteCampo?.fin_at ? 'not-allowed' : 'pointer',
            }}
          >
            {guardando
              ? 'Guardando...'
              : (reporteCampo?.fin_at ? '✓ Confirmar Aplicación' : !reporteCampo?.id ? 'Esperando reporte de campo del boomero' : 'Esperando que boomero finalice')}
          </button>
        ) : (() => {
          // Despacho boom por boom
          const boomActivoItem = boomsProgreso.find(b => b.boom_num === boomActivo);
          // Boleta sin booms calculados (dato anómalo, ej. agua_total=0): no se
          // puede despachar normal. Avisar para que la revisen (no dejar pantalla vacía).
          if (!boomActivoItem) {
            return (
              <div style={{
                width: '100%', padding: '14px 16px', borderRadius: '12px', textAlign: 'center',
                background: 'var(--tone-warn-bg)', border: '1.5px solid var(--tone-warn-border)',
                fontSize: '13.5px', color: 'var(--tone-warn-text)', fontWeight: 700,
              }}>
                ⚠ Esta boleta no tiene booms calculados (volumen 0). Requiere revisión antes de despachar.
              </div>
            );
          }
          if (boomActivoItem.despachado) {
            // Bodega ya cerró este boom. Sub-estado depende del boomero.
            const boomeroFinalizado = boomActivoItem.aplicado_fin_at != null;
            const boomeroIniciado = boomActivoItem.aplicado_inicio_at != null;
            const txt = boomActivoItem.estado === 'descartado'
              ? 'Fracción descartada. La boleta quedó aplicada.'
              : boomeroFinalizado
                ? 'Boom finalizado en campo. Seleccione el siguiente en el wizard.'
                : boomeroIniciado
                  ? '🚜 Boomero aplicando en campo. Espere a que regrese para preparar el siguiente.'
                  : '⌛ Esperando a que el boomero arranque en campo.';
            return (
              <div style={{
                width: '100%', padding: '14px', borderRadius: '14px', textAlign: 'center',
                background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)',
                fontSize: '14px', color: '#86efac', fontWeight: 600,
              }}>
                {txt}
              </div>
            );
          }
          // Bodega prepara cada boom sin esperar al boomero. El único requisito
          // para cerrar es tener todos los productos verificados.
          const esFraccion = boomActivoItem.tipo === 'fraccion';
          const labelCerrar = esFraccion ? '✓ Cerrar Fracción' : `✓ Cerrar Boom ${boomActivoItem.boom_num}`;
          const disabledCerrar = guardando || !todosCheckeados;

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={cerrarBoomActivo}
                disabled={disabledCerrar}
                style={{
                  width: '100%', padding: '18px', borderRadius: '14px', fontSize: '18px', fontWeight: 800,
                  border: 'none', letterSpacing: '0.02em', transition: 'all 0.3s',
                  background: disabledCerrar ? 'rgba(255,255,255,0.07)' : colorAccion,
                  color: disabledCerrar ? 'rgba(255,255,255,0.3)' : '#fff',
                  cursor: disabledCerrar ? 'not-allowed' : 'pointer',
                }}
              >
                {guardando ? 'Guardando...' : todosCheckeados ? labelCerrar : `Verifique los ${totalProds - checkeados.size} producto(s) restantes`}
              </button>
              {esFraccion && (
                <button
                  onClick={() => setModalDescartar({ motivo: '' })}
                  disabled={guardando}
                  style={{
                    width: '100%', padding: '12px', borderRadius: '12px', fontSize: '14px', fontWeight: 700,
                    border: '1.5px solid rgba(239,68,68,0.5)',
                    background: 'rgba(239,68,68,0.08)',
                    color: '#fca5a5', cursor: guardando ? 'not-allowed' : 'pointer',
                    letterSpacing: '0.02em',
                  }}
                >
                  ✕ Descartar fracción (mezcla mixta, no reintegrable)
                </button>
              )}
            </div>
          );
        })()}

        {/* Modal justificación — portal */}
        {modalAjuste !== null && (() => {
          const pm = productos[modalAjuste.idx];
          return createPortal(
            <div style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
            }}>
              <div style={{
                width: '100%', maxWidth: '460px', borderRadius: '18px', padding: '24px',
                background: 'var(--surface-overlay)', border: '1px solid var(--line)',
                boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
              }}>
                <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--ink)', marginBottom: '4px' }}>
                  Justificación de ajuste
                </div>
                <div style={{ fontSize: '14px', color: 'var(--ink-muted)', marginBottom: '20px' }}>
                  {pm.nombre_producto} &nbsp;·&nbsp;
                  Planificado: {fmtN(pm.cantidad_aplicar)} → Real: {fmtN(pm.cantidad_real)} {pm.medida}
                </div>

                {/* Selector de ingeniero destinatario */}
                {ingenieros.length > 0 && (
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--ink-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                      Enviar a
                    </div>
                    <select
                      value={ingenieroSel}
                      onChange={(e) => setIngenieroSel(e.target.value)}
                      style={{
                        width: '100%', padding: '12px 14px', borderRadius: '10px', fontSize: '15px',
                        border: '1px solid var(--line)', background: 'var(--surface-base)',
                        color: 'var(--ink)', cursor: 'pointer',
                      }}
                    >
                      {ingenieros.map((ing) => (
                        <option key={ing.user_id} value={ing.user_id}>
                          {ing.nombre}{ing.rol_nombre ? ` · ${ing.rol_nombre}` : ''}{ing.phone ? ` · WS ${ing.phone}` : ''}
                        </option>
                      ))}
                    </select>
                    {(() => {
                      const sel = ingenieros.find(i => i.user_id === ingenieroSel);
                      if (!sel) return null;
                      const canales = [];
                      if (sel.email) canales.push('Email');
                      if (sel.phone) canales.push('WhatsApp');
                      return (
                        <div style={{ fontSize: '11px', color: 'var(--ink-faint)', marginTop: '4px' }}>
                          Notificara por: {canales.length ? canales.join(' + ') : 'ningun canal (verifique email/telefono)'}
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div style={{ fontSize: '12px', color: 'var(--ink-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                  Motivo
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                  {RAZONES_AJUSTE.map((r, i) => (
                    <button key={r.codigo} onClick={() => setRazSelIdx(i)} style={{
                      padding: '14px 16px', borderRadius: '10px', textAlign: 'left', fontSize: '15px', fontWeight: 600,
                      cursor: 'pointer',
                      border: `1.5px solid ${razSelIdx === i ? '#22c55e' : 'var(--line)'}`,
                      background: razSelIdx === i ? 'rgba(34,197,94,0.12)' : 'var(--surface-raised)',
                      color: razSelIdx === i ? '#4ade80' : 'var(--ink)',
                    }}>{r.label}</button>
                  ))}
                </div>
                {RAZONES_AJUSTE[razSelIdx].codigo === 'OTRO' && (
                  <textarea
                    value={razDetalle}
                    onChange={e => setRazDetalle(e.target.value)}
                    placeholder="Describa el motivo..."
                    rows={3}
                    style={{
                      width: '100%', padding: '12px', borderRadius: '10px', fontSize: '15px',
                      border: '1px solid var(--line)', background: 'var(--surface-base)',
                      color: 'var(--ink)', resize: 'none', marginBottom: '16px',
                    }}
                  />
                )}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => { setModalAjuste(null); setRazDetalle(''); }} style={{
                    flex: 1, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 600,
                    border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: 'pointer',
                  }}>Cancelar</button>
                  <button
                    onClick={confirmarAjuste}
                    disabled={RAZONES_AJUSTE[razSelIdx].codigo === 'OTRO' && !razDetalle.trim()}
                    style={{
                      flex: 2, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 700,
                      border: 'none', background: 'linear-gradient(135deg,#16a34a,#22c55e)', color: '#fff',
                      cursor: RAZONES_AJUSTE[razSelIdx].codigo === 'OTRO' && !razDetalle.trim() ? 'not-allowed' : 'pointer',
                      opacity: RAZONES_AJUSTE[razSelIdx].codigo === 'OTRO' && !razDetalle.trim() ? 0.5 : 1,
                    }}>Confirmar</button>
                </div>
              </div>
            </div>,
            document.body
          );
        })()}

        {modalReversion !== null && (() => {
          const pm = productos[modalReversion.idx];
          const planificado = Number(pm.cantidad_aplicar ?? 0);
          return createPortal(
            <div style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
            }}>
              <div style={{
                width: '100%', maxWidth: '440px', borderRadius: '18px', padding: '24px',
                background: 'var(--surface-overlay)', border: '1px solid var(--line)',
                boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
              }}>
                <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--ink)', marginBottom: '6px' }}>
                  Solicitar reversión
                </div>
                <div style={{ fontSize: '14px', color: 'var(--ink-muted)', lineHeight: 1.55, marginBottom: '18px' }}>
                  Se enviara una nueva solicitud al ingeniero para volver <strong style={{ color: 'var(--ink)' }}>{pm.nombre_producto}</strong> al valor planificado.
                </div>
                <div style={{
                  padding: '12px 14px', borderRadius: '10px', marginBottom: '18px',
                  border: '1px solid rgba(147,197,253,0.22)', background: 'rgba(147,197,253,0.06)',
                  display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center',
                }}>
                  <span style={{ color: 'var(--ink-muted)', fontSize: '13px', fontWeight: 700 }}>Planificado</span>
                  <span style={{ color: 'var(--tone-info-text)', fontSize: '20px', fontWeight: 800 }}>
                    {fmtN(planificado)} {pm.medida}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => setModalReversion(null)} style={{
                    flex: 1, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 600,
                    border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: 'pointer',
                  }}>Cancelar</button>
                  <button
                    onClick={confirmarReversionAjuste}
                    disabled={guardando}
                    style={{
                      flex: 2, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 800,
                      border: 'none', background: guardando ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg,#f59e0b,#fbbf24)',
                      color: guardando ? 'rgba(255,255,255,0.45)' : '#1f1300',
                      cursor: guardando ? 'not-allowed' : 'pointer',
                    }}>Enviar solicitud</button>
                </div>
              </div>
            </div>,
            document.body
          );
        })()}

        {/* Modal: solicitar reversión de boleta completa al ingeniero */}
        {modalSolicReversion !== null && boletaSel && createPortal(
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
          }}>
            <div style={{
              width: '100%', maxWidth: '500px', borderRadius: '18px', padding: '24px',
              background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
            }}>
              <div style={{ fontSize: '20px', fontWeight: 800, color: '#fbbf24', marginBottom: '6px' }}>
                ↺ Solicitar reversión de boleta
              </div>
              <div style={{ fontSize: '14px', color: 'var(--ink-muted)', lineHeight: 1.55, marginBottom: '14px' }}>
                Se enviará una solicitud al ingeniero para revertir la boleta <strong style={{ color: 'var(--ink)' }}>{boletaSel.numero}</strong> al estado <strong style={{ color: 'var(--ink)' }}>PENDIENTE</strong>.
              </div>
              <div style={{
                padding: '10px 12px', borderRadius: '10px', marginBottom: '16px',
                border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.05)',
                fontSize: '12px', color: '#fde68a', lineHeight: 1.5,
              }}>
                ⚠ Si la solicitud es aprobada, se borrará todo el trabajo del boomero: reporte de aplicación, recorrido GPS y booms aplicados.
              </div>
              <div style={{ marginBottom: '18px' }}>
                <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Motivo *
                </div>
                <textarea
                  value={modalSolicReversion.motivo}
                  onChange={(e) => setModalSolicReversion({ motivo: e.target.value })}
                  placeholder="Explicá por qué se necesita revertir la boleta…"
                  autoFocus
                  rows={4}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '10px', fontSize: '14px',
                    border: '1px solid var(--line)', background: 'var(--surface-base)', color: 'var(--ink)',
                    fontFamily: 'inherit', resize: 'vertical', outline: 'none',
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setModalSolicReversion(null)} disabled={guardando} style={{
                  flex: 1, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 600,
                  border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: guardando ? 'not-allowed' : 'pointer',
                }}>Cancelar</button>
                <button
                  onClick={confirmarSolicReversion}
                  disabled={guardando || !modalSolicReversion.motivo.trim()}
                  style={{
                    flex: 2, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 800,
                    border: 'none',
                    background: (guardando || !modalSolicReversion.motivo.trim())
                      ? 'rgba(255,255,255,0.1)'
                      : 'linear-gradient(135deg,#f59e0b,#fbbf24)',
                    color: (guardando || !modalSolicReversion.motivo.trim()) ? 'rgba(255,255,255,0.45)' : '#1f1300',
                    cursor: (guardando || !modalSolicReversion.motivo.trim()) ? 'not-allowed' : 'pointer',
                  }}>
                  {guardando ? 'Enviando…' : 'Enviar solicitud'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Modal: descartar fracción (mezcla mixta no reintegrable) */}
        {modalDescartar !== null && boletaSel && createPortal(
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
          }}>
            <div style={{
              width: '100%', maxWidth: '500px', borderRadius: '18px', padding: '24px',
              background: 'var(--tone-warn-bg)', border: '1px solid #ef4444',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
            }}>
              <div style={{ fontSize: '20px', fontWeight: 800, color: '#fca5a5', marginBottom: '6px' }}>
                ✕ Descartar fracción
              </div>
              <div style={{ fontSize: '14px', color: 'var(--ink-muted)', lineHeight: 1.55, marginBottom: '14px' }}>
                La fracción contiene mezcla química y <strong style={{ color: '#fca5a5' }}>no puede reintegrarse a bodega</strong>. Al descartarla, la boleta pasará a estado <strong style={{ color: 'var(--ink)' }}>aplicada</strong> y se descontará el inventario de los booms ya cerrados.
              </div>
              <div style={{
                padding: '10px 12px', borderRadius: '10px', marginBottom: '16px',
                border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.05)',
                fontSize: '12px', color: '#fca5a5', lineHeight: 1.5,
              }}>
                ⚠ Esta acción no se puede revertir desde bodega. Si fue por error, solicite reversión al ingeniero.
              </div>
              <div style={{ marginBottom: '18px' }}>
                <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Motivo *
                </div>
                <textarea
                  value={modalDescartar.motivo}
                  onChange={(e) => setModalDescartar({ motivo: e.target.value })}
                  placeholder="Explique por qué se descarta la fracción (ej. mezcla incompatible, derrame, contaminación...)"
                  autoFocus
                  rows={4}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '10px', fontSize: '14px',
                    border: '1px solid var(--line)', background: 'var(--surface-base)', color: 'var(--ink)',
                    fontFamily: 'inherit', resize: 'vertical', outline: 'none',
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setModalDescartar(null)} disabled={guardando} style={{
                  flex: 1, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 600,
                  border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                }}>Cancelar</button>
                <button
                  onClick={() => descartarFraccion(modalDescartar.motivo)}
                  disabled={guardando || !modalDescartar.motivo.trim()}
                  style={{
                    flex: 2, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 800,
                    border: 'none',
                    background: (guardando || !modalDescartar.motivo.trim())
                      ? 'rgba(255,255,255,0.1)'
                      : 'linear-gradient(135deg,#dc2626,#ef4444)',
                    color: (guardando || !modalDescartar.motivo.trim()) ? 'rgba(255,255,255,0.45)' : '#fff',
                    cursor: (guardando || !modalDescartar.motivo.trim()) ? 'not-allowed' : 'pointer',
                  }}>
                  {guardando ? 'Descartando…' : 'Confirmar descarte'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Modal: solicitar cambio de producto al ingeniero */}
        {modalCambioProd !== null && boletaSel && (() => {
          const p = productos.find(pp => pp.id === modalCambioProd.productoId);
          if (!p) return null;
          return createPortal(
            <div style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
            }}>
              <div style={{
                width: '100%', maxWidth: '520px', borderRadius: '18px', padding: '24px',
                background: 'var(--tone-purple-bg)', border: '1px solid var(--tone-purple-border)',
                boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
              }}>
                <div style={{ fontSize: '20px', fontWeight: 800, color: '#a78bfa', marginBottom: '6px' }}>
                  🔄 Cambiar producto
                </div>
                <div style={{ fontSize: '14px', color: 'var(--ink-muted)', lineHeight: 1.55, marginBottom: '14px' }}>
                  Se enviará una solicitud al ingeniero para reemplazar el producto. Al aprobar, elegirá el sustituto del catálogo del plan de manejo.
                </div>
                <div style={{
                  padding: '10px 12px', borderRadius: '10px', marginBottom: '16px',
                  border: '1px solid var(--line)', background: 'var(--surface-deep)',
                  fontSize: '13px', color: 'var(--ink)',
                }}>
                  <div style={{ fontSize: '10px', color: 'var(--ink-faint)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Producto actual</div>
                  <div style={{ fontWeight: 700 }}>{p.nombre_producto}</div>
                  <div style={{ fontSize: '11px', color: 'var(--ink-muted)', marginTop: '2px' }}>
                    {p.articulo} · {fmtN(p.cantidad_aplicar)} {p.medida} · dosis {fmtN(p.dosis)}
                  </div>
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Motivo *
                  </div>
                  <textarea
                    value={modalCambioProd.motivo}
                    onChange={(e) => setModalCambioProd({ ...modalCambioProd, motivo: e.target.value })}
                    placeholder="Sin stock, vencido, sustituto técnico…"
                    autoFocus
                    rows={3}
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: '10px', fontSize: '14px',
                      border: '1px solid var(--line)', background: 'var(--surface-base)', color: 'var(--ink)',
                      fontFamily: 'inherit', resize: 'vertical', outline: 'none',
                    }}
                  />
                </div>
                <div style={{ marginBottom: '18px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Sugerido <span style={{ fontWeight: 500, textTransform: 'none' }}>(opcional)</span>
                  </div>
                  <input
                    type="text"
                    value={modalCambioProd.sugerido}
                    onChange={(e) => setModalCambioProd({ ...modalCambioProd, sugerido: e.target.value })}
                    placeholder="Nombre o código del sustituto que recomendás"
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: '10px', fontSize: '14px',
                      border: '1px solid var(--line)', background: 'var(--surface-base)', color: 'var(--ink)',
                      outline: 'none',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => setModalCambioProd(null)} disabled={guardando} style={{
                    flex: 1, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 600,
                    border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: guardando ? 'not-allowed' : 'pointer',
                  }}>Cancelar</button>
                  <button
                    onClick={confirmarCambioProd}
                    disabled={guardando || !modalCambioProd.motivo.trim()}
                    style={{
                      flex: 2, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 800,
                      border: 'none',
                      background: (guardando || !modalCambioProd.motivo.trim())
                        ? 'rgba(255,255,255,0.1)'
                        : 'linear-gradient(135deg,#7c3aed,#a78bfa)',
                      color: (guardando || !modalCambioProd.motivo.trim()) ? 'rgba(255,255,255,0.45)' : '#fff',
                      cursor: (guardando || !modalCambioProd.motivo.trim()) ? 'not-allowed' : 'pointer',
                    }}>
                    {guardando ? 'Enviando…' : 'Enviar solicitud'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          );
        })()}

        {/* Modal: solicitar EXCLUIR un producto de la boleta */}
        {modalExcluirProd !== null && boletaSel && (() => {
          const p = productos.find(pp => pp.id === modalExcluirProd.productoId);
          if (!p) return null;
          return createPortal(
            <div style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
            }}>
              <div style={{
                width: '100%', maxWidth: '520px', borderRadius: '18px', padding: '24px',
                background: 'var(--tone-warn-bg)', border: '1px solid #ef4444',
                boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
              }}>
                <div style={{ fontSize: '20px', fontWeight: 800, color: '#fca5a5', marginBottom: '6px' }}>
                  ⛔ Excluir producto
                </div>
                <div style={{ fontSize: '14px', color: 'var(--ink-muted)', lineHeight: 1.55, marginBottom: '14px' }}>
                  Se enviará una solicitud al ingeniero. Si aprueba, el producto NO se despachará ni se aplicará.
                </div>
                <div style={{
                  padding: '10px 12px', borderRadius: '10px', marginBottom: '16px',
                  border: '1px solid var(--line)', background: 'var(--surface-deep)',
                  fontSize: '13px', color: 'var(--ink)',
                }}>
                  <div style={{ fontSize: '10px', color: 'var(--ink-faint)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Producto</div>
                  <div style={{ fontWeight: 700 }}>{p.nombre_producto}</div>
                  <div style={{ fontSize: '11px', color: 'var(--ink-muted)', marginTop: '2px' }}>
                    {p.articulo} · {fmtN(p.cantidad_aplicar)} {p.medida} · dosis {fmtN(p.dosis)}
                  </div>
                </div>
                <div style={{ marginBottom: '18px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Motivo *
                  </div>
                  <textarea
                    value={modalExcluirProd.motivo}
                    onChange={(e) => setModalExcluirProd({ ...modalExcluirProd, motivo: e.target.value })}
                    placeholder="Sin stock, decisión técnica, producto vencido…"
                    autoFocus
                    rows={3}
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: '10px', fontSize: '14px',
                      border: '1px solid var(--line)', background: 'var(--surface-base)', color: 'var(--ink)',
                      fontFamily: 'inherit', resize: 'vertical', outline: 'none',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => setModalExcluirProd(null)} disabled={guardando} style={{
                    flex: 1, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 600,
                    border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: guardando ? 'not-allowed' : 'pointer',
                  }}>Cancelar</button>
                  <button
                    onClick={confirmarExcluirProd}
                    disabled={guardando || !modalExcluirProd.motivo.trim()}
                    style={{
                      flex: 2, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 800,
                      border: 'none',
                      background: (guardando || !modalExcluirProd.motivo.trim())
                        ? 'rgba(255,255,255,0.1)'
                        : 'linear-gradient(135deg,#b91c1c,#ef4444)',
                      color: (guardando || !modalExcluirProd.motivo.trim()) ? 'rgba(255,255,255,0.45)' : '#fff',
                      cursor: (guardando || !modalExcluirProd.motivo.trim()) ? 'not-allowed' : 'pointer',
                    }}>
                    {guardando ? 'Enviando…' : 'Enviar solicitud'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          );
        })()}

        {/* Modal: solicitar AGREGAR un producto nuevo a la boleta */}
        {modalAgregarProd !== null && boletaSel && createPortal(
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
          }}>
            <div style={{
              width: '100%', maxWidth: '520px', borderRadius: '18px', padding: '24px',
              background: 'var(--tone-purple-bg)', border: '1px solid var(--tone-purple-border)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
            }}>
              <div style={{ fontSize: '20px', fontWeight: 800, color: '#a78bfa', marginBottom: '6px' }}>
                ➕ Agregar producto
              </div>
              <div style={{ fontSize: '14px', color: 'var(--ink-muted)', lineHeight: 1.55, marginBottom: '14px' }}>
                Se enviará una solicitud al ingeniero para agregar un producto a esta boleta. Al aprobar, elegirá cuál del plan de manejo y la dosis a aplicar.
              </div>
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Motivo *
                </div>
                <textarea
                  value={modalAgregarProd.motivo}
                  onChange={(e) => setModalAgregarProd({ ...modalAgregarProd, motivo: e.target.value })}
                  placeholder="Producto faltante, ajuste agronómico, refuerzo…"
                  autoFocus
                  rows={3}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '10px', fontSize: '14px',
                    border: '1px solid var(--line)', background: 'var(--surface-base)', color: 'var(--ink)',
                    fontFamily: 'inherit', resize: 'vertical', outline: 'none',
                  }}
                />
              </div>
              <div style={{ marginBottom: '18px' }}>
                <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Sugerido <span style={{ fontWeight: 500, textTransform: 'none' }}>(opcional)</span>
                </div>
                <input
                  type="text"
                  value={modalAgregarProd.sugerido}
                  onChange={(e) => setModalAgregarProd({ ...modalAgregarProd, sugerido: e.target.value })}
                  placeholder="Nombre o código del producto que recomendás"
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '10px', fontSize: '14px',
                    border: '1px solid var(--line)', background: 'var(--surface-base)', color: 'var(--ink)',
                    outline: 'none',
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setModalAgregarProd(null)} disabled={guardando} style={{
                  flex: 1, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 600,
                  border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: guardando ? 'not-allowed' : 'pointer',
                }}>Cancelar</button>
                <button
                  onClick={confirmarAgregarProd}
                  disabled={guardando || !modalAgregarProd.motivo.trim()}
                  style={{
                    flex: 2, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 800,
                    border: 'none',
                    background: (guardando || !modalAgregarProd.motivo.trim())
                      ? 'rgba(255,255,255,0.1)'
                      : 'linear-gradient(135deg,#7c3aed,#a78bfa)',
                    color: (guardando || !modalAgregarProd.motivo.trim()) ? 'rgba(255,255,255,0.45)' : '#fff',
                    cursor: (guardando || !modalAgregarProd.motivo.trim()) ? 'not-allowed' : 'pointer',
                  }}>
                  {guardando ? 'Enviando…' : 'Enviar solicitud'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
        </div>
      </div>
    );
  }

  // ── V°B° Ingeniero ────────────────────────────────────────────────────────

  if (vista === 'vob' && boletaSel) {
    const sinReporte = !reporteCampo?.id;
    const sinFinalizar = !!reporteCampo?.id && !reporteCampo?.fin_at;
    const bloqueado = sinReporte || sinFinalizar;
    return (
      <VistaVoBo
        boletaSel={boletaSel}
        reporteCampo={reporteCampo}
        loteGeojson={loteGeojson}
        tracks={tracks}
        anchoBoom={anchoBoom}
        sinReporte={sinReporte}
        sinFinalizar={sinFinalizar}
        bloqueado={bloqueado}
        guardando={guardando}
        darVoBo={darVoBo}
        onVolver={() => { setVista('lista'); setBoletaSel(null); }}
        renderMsgBanner={renderMsgBanner}
      />
    );
  }

  return null;
}
